// src/pages/accounting/ProjectProfitabilityPage.jsx
// ─────────────────────────────────────────────────────────────────
// Project Profitability — Per-project graphs of expenses vs income
//
// Each project shows a line chart over its duration:
//   Line 1: Monthly cost (labour + expenses)
//   Line 2: Received fee (paid invoices)
//
// Period selectors: Month, Calendar Year, Indian FY, Total project, Custom range
// (same as ProjectCostLedgerPage)
// ─────────────────────────────────────────────────────────────────

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import {
  ProfitabilityChart,
  ProfitabilityBarChart,
  CompareChart,
  AggregateKPIBar,
  buildAggregateByTime,
  buildAggregateByStage,
  buildBarAggregateByMonth,
  bucketChartData,
  selectBarBucket,
  extractStageMarkers as extractStageMarkersHelper,
  computeSharedDomain,
  mergeProjectProfitLines,
  COMPARE_COLORS,
} from './profitabilityChartHelpers';
import { validateSalaryConservation } from '../../utils/validateSalaryConservation';
import { computeProjectLabour, computeInternalCostRedistribution } from '../../utils/projectLabour';
import { computeActivityOverheadEngine, getProjectMonths as computeProjectMonths } from '../../utils/activityOverheadEngine';
import {
  BrioAnalyticsSurface,
  BrioFrame,
  BrioMonthPicker,
} from '../../components/BrioComponents';
import { supabase } from '../../supabaseClient';

// ════════════════════════════════════════════════════════════════
// BRIO DESIGN SYSTEM (matches AnalyticsDashboardPage)
// ════════════════════════════════════════════════════════════════

// AUDIT: Local color map duplicates BRIO tokens as hardcoded hex; #A66257, #537767 not in approved palette
const brioColors = {
  gold: '#9F902A',
  goldHover: '#7F7321',
  goldLight: 'rgba(159, 144, 42, 0.1)',
  goldMedium: 'rgba(159, 144, 42, 0.3)',
  white: '#ffffff',
  black: '#111111',
  grey: '#666666',
  greyLight: '#f5f5f5',
  border: 'rgba(0, 0, 0, 0.1)',
  expense: '#A66257',       // warm red — outgoing
  expenseLight: 'rgba(166, 98, 87, 0.15)',
  income: '#537767',        // teal green — incoming
  incomeLight: 'rgba(83, 119, 103, 0.15)',
  profit: '#9F902A',        // gold — profit
};

const brioStyles = {
  fontFamily: 'Raleway, sans-serif',
  fontWeightLight: 200,
  fontWeightRegular: 300,
  fontWeightMedium: 400,
};

// ════════════════════════════════════════════════════════════════
// MONTH UTILITIES (shared with ProjectCostLedgerPage)
// ════════════════════════════════════════════════════════════════

const ym = (year, month) => `${year}-${String(month).padStart(2, '0')}`;

const parseYM = (s) => {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{1,2})/);
  return m ? { year: parseInt(m[1]), month: parseInt(m[2]) } : null;
};

function monthRange(startYM, endYM) {
  const s = parseYM(startYM);
  const e = parseYM(endYM);
  if (!s || !e) return [];
  const months = [];
  let y = s.year, m = s.month;
  while (y < e.year || (y === e.year && m <= e.month)) {
    months.push(ym(y, m));
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return months;
}

const todayYM = () => {
  const d = new Date();
  return ym(d.getFullYear(), d.getMonth() + 1);
};

const shortMonth = (ymStr) => {
  const p = parseYM(ymStr);
  if (!p) return ymStr;
  const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${names[p.month - 1]} ${String(p.year).slice(2)}`;
};

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ════════════════════════════════════════════════════════════════
// ID / AUTH HELPERS
// ════════════════════════════════════════════════════════════════

const toNumericId = (id) => {
  if (typeof id === 'number') return id;
  if (typeof id === 'string' && /^-?\d+$/.test(id.trim())) return parseInt(id, 10);
  return id;
};

// ════════════════════════════════════════════════════════════════
// EXPENSE CATEGORIES
// ════════════════════════════════════════════════════════════════

function normalizeExpenseCategory(cat) {
  if (!cat) return 'Miscellaneous';
  const lower = cat.toLowerCase();
  if (lower.includes('consult')) return 'Consultants';
  if (lower.includes('travel')) return 'Travel';
  if (lower.includes('material') || lower.includes('print') || lower.includes('model') || lower.includes('render')) return 'Materials';
  return 'Miscellaneous';
}

// (CustomTooltip moved to profitabilityChartHelpers.js)


// ════════════════════════════════════════════════════════════════
// COMPONENT
// ════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════
// DORMANCY CARRYING COST — CONFIGURATION
// ════════════════════════════════════════════════════════════════
//
// Dormancy policy: Reserve a small configurable percentage of the monthly
// office overhead pool as a "dormancy pool". This pool is distributed evenly
// across dormant open projects in that office-month.
//
// A project is considered dormant for a given month when ALL of:
//   1. project.status !== 'completed' and !== 'archived' (still open)
//   2. the project has zero allocable hours in that office-month
//   3. the project has at least one time entry in any other month (evidence of life)
//
// The remainder (1 - dormancyPoolPct) is the operational pool, allocated
// by hour share as before.
//
// The percentage is configurable via app_settings key 'dormancyPoolPct' (default: 3%).

export default function ProjectProfitabilityPage({
  activeProjects,
  activeClients,
  activeUsers,
  activeTimeEntries,
  allTimeEntriesLoaded = false,
  timeEntriesLoadProgress = 0,
  fetchAllTimeEntries,
  activeExpenses,
  activeOfficeOverheads = [],
  monthlyOfficeOverheads = [],
  monthlyOfficeOverheadsLoading = false,
  calcMonthlyCost,
  isEmployerBurdenCategory,
  fullLedgerLoaded,
  fullSalaryLedger = {},
  getUserProfileAtDate,
  getEffectiveContractedHours,
  computeOverheadAllocation,
  fmtCurr,
  currSymbol,
  settingsHook,
  isAdmin,
  getSupervisedProjects,
  projectStatusFilter,
  setProjectStatusFilter,
  // Callable supplied by App.js: writes per-project loaded cost to the
  // project_loaded_costs cache so the Inquiries Fee Intelligence drawer
  // can read it without recomputing.
  refreshProjectLoadedCosts,
}) {
  // calcMonthlyCost prop here is calcMonthlyTrueCost from App.js — it returns
  // base + bonus + pro-rata employer burden (NL only, non-directors). No
  // markup is applied on this page; markup is a target-revenue concept that
  // belongs on the Salary Cost report, not on the cost side of P&L.
  // See methodology section 3 ("Overhead") for the full rationale.
  const isEmployerBurden = isEmployerBurdenCategory
    || ((category) => category === 'Pension' || category === 'Salary Tax');
  // ──────────────────────────────────────────────────────
  // STATE: Period selection (same as ProjectCostLedgerPage)
  // ──────────────────────────────────────────────────────
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const [periodType, setPeriodType] = useState('total');
  const [selYear, setSelYear] = useState(currentYear);
  const [selMonth, setSelMonth] = useState(currentMonth);
  const [fyYear, setFyYear] = useState(currentMonth >= 4 ? currentYear : currentYear - 1);
  const [cyYear, setCyYear] = useState(currentYear);
  const [customStart, setCustomStart] = useState(ym(currentYear, 1));
  const [customEnd, setCustomEnd] = useState(ym(currentYear, currentMonth));
  const [expandedProjects, setExpandedProjects] = useState({});
  const [expandedLedgers, setExpandedLedgers] = useState({});
  const [showReconciliation, setShowReconciliation] = useState(false);
  const [showMethodology, setShowMethodology] = useState(false);

  // ── Visualization controls (new) ──
  const [chartMode, setChartMode] = useState('financial');     // 'financial' | 'performance'
  const [chartType, setChartType] = useState('area');          // 'area' | 'bar'
  const [scaleMode, setScaleMode] = useState('local');         // 'local' | 'shared'
  const [aggregateXAxis, setAggregateXAxis] = useState('time'); // 'time' | 'stage'
  const [aggregateBarMode, setAggregateBarMode] = useState('auto'); // 'auto' | 'fy' | 'cy'
  const [selectedProjectIds, setSelectedProjectIds] = useState({});
  const [multiProjectMode, setMultiProjectMode] = useState('compare'); // 'compare' | 'combined'
  const [showAggregate, setShowAggregate] = useState(false);

  // Essential profit % target from settings (for profitability band)
  const essentialProfitPct = settingsHook?.essentialProfitPercentage ?? 20;

  // Dormancy pool fraction (from app_settings, default 3%)
  const DORMANCY_POOL_PCT = (settingsHook?.dormancyPoolPct ?? 3) / 100;

  // Status filter
  const statusFilter = projectStatusFilter ?? 'all';

  // ── Visible projects: scoped for supervisors, full for admins ──
  const visibleProjects = useMemo(() => {
    if (isAdmin) return activeProjects;
    if (getSupervisedProjects) return getSupervisedProjects();
    return activeProjects;
  }, [isAdmin, activeProjects, getSupervisedProjects]);

  // ──────────────────────────────────────────────────────
  // Currency-aware formatting (with FX conversion, rounded to 100s, no decimals)
  // ──────────────────────────────────────────────────────
  const isEuro = currSymbol === '€';
  const displayCurrency = settingsHook?.displayCurrency || 'INR';
  const baseCurrency = settingsHook?.baseCurrency || 'INR';

  // Convert a base-currency amount to display currency
  const toDisplay = useCallback((amount, fxDate) => {
    if (!Number.isFinite(amount)) return 0;
    if (settingsHook?.convertMoney) {
      return settingsHook.convertMoney(amount, baseCurrency, displayCurrency, fxDate);
    }
    return amount;
  }, [settingsHook, baseCurrency, displayCurrency]);

  // Convert an amount held in a project's contract currency (fee, retainer,
  // misc scopes) to display currency. These carry no date of their own, so
  // the current month's rate is used.
  const contractToDisplay = useCallback((amount, contractCurrency) => {
    if (!Number.isFinite(amount)) return 0;
    const from = contractCurrency || 'INR';
    if (from === displayCurrency || !settingsHook?.convertMoney) return amount;
    const today = new Date();
    const fxDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
    return settingsHook.convertMoney(amount, from, displayCurrency, fxDate);
  }, [settingsHook, displayCurrency]);

  // Format a display-currency amount with tiered rounding:
  // >= 1000: round to 100s, no decimals
  // >= 10 and < 1000: round to 10s, no decimals
  // < 10: show 2 decimals
  const fmt = useCallback((amount) => {
    if (!Number.isFinite(amount)) amount = 0;
    const abs = Math.abs(amount);
    let rounded, decimals = 0;
    if (abs < 10) {
      rounded = Math.round(amount * 100) / 100;
      decimals = 2;
    } else if (abs < 1000) {
      rounded = Math.round(amount / 10) * 10;
      decimals = 0;
    } else {
      rounded = Math.round(amount / 100) * 100;
      decimals = 0;
    }
    const locale = isEuro ? 'nl-NL' : 'en-IN';
    const symbol = isEuro ? '€' : '₹';
    return `${symbol}${rounded.toLocaleString(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  }, [isEuro]);

  // Y-axis tick formatter (values are already in display currency after chart data conversion)
  const tickFmt = useCallback((v) => {
    if (isEuro) {
      if (Math.abs(v) >= 1000000) return `€${(v / 1000000).toFixed(1)}M`;
      if (Math.abs(v) >= 1000) return `€${(v / 1000).toFixed(0)}K`;
      return `€${Math.round(v)}`;
    }
    if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
    if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
    if (Math.abs(v) >= 1000) return `₹${(v / 1000).toFixed(0)}K`;
    return `₹${Math.round(v)}`;
  }, [isEuro]);

  // ──────────────────────────────────────────────────────
  // INVOICES: Fetch only for visible projects
  // ──────────────────────────────────────────────────────
  const [allInvoices, setAllInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);

  const visibleProjectIds = useMemo(
    () => visibleProjects.map(p => p.id),
    [visibleProjects]
  );

  // Content-stable key for the ID set: visibleProjects is freshly constructed
  // on every render for supervisors (getSupervisedProjects() returns a new
  // array), which would otherwise re-fire the invoice + expense-payment fetches
  // on every render.
  const visibleProjectIdsKey = useMemo(
    () => [...visibleProjectIds].map(id => String(id)).sort().join(','),
    [visibleProjectIds]
  );

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      if (visibleProjectIds.length === 0) {
        setAllInvoices([]);
        setInvoicesLoading(false);
        return;
      }
      setInvoicesLoading(true);
      try {
        const { data, error } = await supabase
          .from('v_invoices_reporting')
          .select('id, project_id, stage_key, invoice_date, base_amount, gross_amount, net_amount, base_amount_inr_default, currency, status, paid_confirmed, paid_date')
          .is('deleted_at', null)
          .in('project_id', visibleProjectIds);
        if (error) throw error;
        if (!cancelled) {
          setAllInvoices((data || []).map(r => ({
            id: r.id,
            projectId: r.project_id,
            stageKey: r.stage_key ?? null,
            invoiceDate: r.invoice_date,
            baseAmount: r.base_amount ?? 0,
            grossAmount: r.gross_amount ?? 0,
            netAmount: r.net_amount ?? 0,
            baseAmountInrDefault: r.base_amount_inr_default != null ? Number(r.base_amount_inr_default) : null,
            currency: r.currency ?? 'INR',
            status: r.status ?? 'draft',
            paidConfirmed: r.paid_confirmed ?? false,
            paidDate: r.paid_date ?? null,
          })));
        }
      } catch (err) {
        console.error('[ProjectProfitability] Error fetching invoices:', err);
      } finally {
        if (!cancelled) setInvoicesLoading(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleProjectIdsKey]);

  // ──────────────────────────────────────────────────────
  // EXPENSE PAYMENTS: Client reimbursements (bank_payment_events with expense_payment=true)
  // ──────────────────────────────────────────────────────
  const [expensePayments, setExpensePayments] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (visibleProjectIds.length === 0) {
      setExpensePayments([]);
      return () => { cancelled = true; };
    }
    supabase
      .from('bank_payment_events')
      .select('id, amount, txn_date, linked_project_id')
      .eq('expense_payment', true)
      .eq('status', 'expense')
      .in('linked_project_id', visibleProjectIds)
      .then(({ data }) => {
        if (!cancelled && data) {
          setExpensePayments(data.map(r => ({
            amount: Number(r.amount) || 0,
            txnDate: r.txn_date ?? null,
            linkedProjectId: r.linked_project_id ? Number(r.linked_project_id) : null,
          })));
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleProjectIdsKey]);

  // Index: client deduction by (projectId, yearMonth) in INR
  const clientDeductionByProjectMonth = useMemo(() => {
    const idx = {};
    expensePayments.forEach(p => {
      if (!p.linkedProjectId || !p.txnDate) return;
      const ymKey = p.txnDate.substring(0, 7);
      const pid = p.linkedProjectId;
      if (!idx[pid]) idx[pid] = {};
      idx[pid][ymKey] = (idx[pid][ymKey] || 0) + p.amount;
    });
    return idx;
  }, [expensePayments]);

  // Time entries are gated alongside the other sources: without them, project
  // rows compute against an empty lookup and render as zeros, which previously
  // forced users to navigate away and back to see complete data.
  const dataLoading = invoicesLoading
    || monthlyOfficeOverheadsLoading
    || !fullLedgerLoaded
    || !allTimeEntriesLoaded;

  // Real % while time entries are paging (the longest fetch); pinned at 99%
  // if they finish first but other sources are still resolving.
  const overallProgress = !dataLoading
    ? 0
    : allTimeEntriesLoaded
      ? 99
      : timeEntriesLoadProgress;

  // Always fetch all time entries when the page mounts (needed for accurate project data)
  useEffect(() => {
    if (!allTimeEntriesLoaded && fetchAllTimeEntries) {
      fetchAllTimeEntries();
    }
  }, [allTimeEntriesLoaded, fetchAllTimeEntries]);

  // ──────────────────────────────────────────────────────
  // DERIVED: Selected period months
  // ──────────────────────────────────────────────────────
  const selectedMonths = useMemo(() => {
    switch (periodType) {
      case 'month':
        return [ym(selYear, selMonth)];
      case 'indianFY':
        return monthRange(ym(fyYear, 4), ym(fyYear + 1, 3));
      case 'calendarYear':
        return monthRange(ym(cyYear, 1), ym(cyYear, 12));
      case 'custom':
        return monthRange(customStart, customEnd);
      case 'total':
        return [];
      default:
        return [ym(selYear, selMonth)];
    }
  }, [periodType, selYear, selMonth, fyYear, cyYear, customStart, customEnd]);

  const periodLabel = useMemo(() => {
    switch (periodType) {
      case 'month': return `${MONTH_NAMES[selMonth - 1]} ${selYear}`;
      case 'indianFY': return `FY ${fyYear}–${String(fyYear + 1).slice(2)}`;
      case 'calendarYear': return `${cyYear}`;
      case 'custom': return `${shortMonth(customStart)} – ${shortMonth(customEnd)}`;
      case 'total': return 'Total Project Duration';
      default: return '';
    }
  }, [periodType, selYear, selMonth, fyYear, cyYear, customStart, customEnd]);

  // ──────────────────────────────────────────────────────
  // INDEX: Time entries by (projectId, userId, yearMonth)
  // ──────────────────────────────────────────────────────
  const { byProject, byUserMonth } = useMemo(() => {
    const bp = {};
    const bum = {};
    activeTimeEntries.forEach(e => {
      const ymKey = e.date.substring(0, 7);
      const hours = (e.duration || 0) / 3600;
      const pid = e.projectId;
      const uid = e.userId;
      if (!bp[pid]) bp[pid] = {};
      if (!bp[pid][uid]) bp[pid][uid] = {};
      bp[pid][uid][ymKey] = (bp[pid][uid][ymKey] || 0) + hours;
      if (!bum[uid]) bum[uid] = {};
      bum[uid][ymKey] = (bum[uid][ymKey] || 0) + hours;
    });
    return { byProject: bp, byUserMonth: bum };
  }, [activeTimeEntries]);

  // ──────────────────────────────────────────────────────
  // INDEX: Expenses by (projectId, yearMonth)
  // ──────────────────────────────────────────────────────
  const expenseByProjectMonth = useMemo(() => {
    const idx = {};
    activeExpenses.forEach(e => {
      if (e.status === 'planned') return;
      const pid = e.projectId;
      const ymKey = (e.date || '').substring(0, 7);
      if (!ymKey) return;
      if (!idx[pid]) idx[pid] = {};
      idx[pid][ymKey] = (idx[pid][ymKey] || 0) + (e.amount || 0);
    });
    return idx;
  }, [activeExpenses]);

  // ──────────────────────────────────────────────────────
  // ACTIVITY-BASED OVERHEAD + DORMANCY CARRYING COST
  //
  // Overhead source priority:
  //   1. office_overheads_monthly (true monthly data, preferred)
  //   2. office_overheads_yearly / 12 (fallback if no monthly row)
  //   3. 0 with 'no_overhead_pool' or 'monthly_data_missing' flag
  //
  // The office-month pool is split:
  //   - operational pool = pool × (1 - DORMANCY_POOL_PCT)
  //   - dormancy pool    = pool × DORMANCY_POOL_PCT
  //
  // Operational overhead: allocated to active projects by hour share
  // Dormancy carrying cost: allocated evenly to dormant open projects
  //
  // Returns { byProjectMonth, dormancyByProjectMonth,
  //           officeMonthReconciliation, projectMonthDebug }
  // ──────────────────────────────────────────────────────

  // Pre-index: monthly overhead rows by office+month.
  // Excludes employer-burden categories (Pension, Salary Tax). Those are
  // now attributed to NL users' Direct Labour via calcMonthlyTrueCost,
  // not distributed across the office by hour-share.
  const monthlyOHIndex = useMemo(() => {
    const idx = {}; // { 'NL|YYYY-MM': totalAmountInr }
    monthlyOfficeOverheads.forEach(o => {
      if (isEmployerBurden(o.category)) return;
      const loc = (o.officeLocation || 'NL') === 'IN' ? 'IN' : 'NL';
      const key = `${loc}|${o.month}`;
      idx[key] = (idx[key] || 0) + (o.amountInr ?? 0);
    });
    return idx;
  }, [monthlyOfficeOverheads, isEmployerBurden]);

  // Pre-index: yearly overhead for fallback. Same exclusion as above.
  const yearlyOHIndex = useMemo(() => {
    const idx = {}; // { 'NL': { year: totalINR } }
    activeOfficeOverheads.forEach(o => {
      if (isEmployerBurden(o.category)) return;
      const loc = (o.officeLocation || 'NL') === 'IN' ? 'IN' : 'NL';
      const yr = o.year;
      if (!idx[loc]) idx[loc] = {};
      idx[loc][yr] = (idx[loc][yr] || 0) + (o.amountInInr ?? o.amount ?? 0);
    });
    return idx;
  }, [activeOfficeOverheads, isEmployerBurden]);

  // Pre-index: which projects have ANY time entries (evidence of life for dormancy)
  const projectsWithAnyTime = useMemo(() => {
    const s = new Set();
    activeTimeEntries.forEach(e => {
      if (e.projectId && (e.duration || 0) > 0) s.add(toNumericId(e.projectId));
    });
    return s;
  }, [activeTimeEntries]);

  // Stable set of internal project IDs (client = BRIO). Defined here
  // before the overhead engine so the engine can take it as input.
  const internalProjectIds = useMemo(() => {
    const clientMap = {};
    activeClients.forEach(c => { clientMap[toNumericId(c.id)] = c; });
    const ids = new Set();
    activeProjects.forEach(p => {
      const client = clientMap[toNumericId(p.clientId)];
      if (!client || !p.clientId || (client.name || '').toLowerCase() === 'brio') {
        ids.add(toNumericId(p.id));
      }
    });
    return ids;
  }, [activeProjects, activeClients]);

  // ──────────────────────────────────────────────────────
  // SALARY UTILIZATION GAP (Portfolio Aggregate, admin only)
  // ──────────────────────────────────────────────────────
  // Per §2.8 of the methodology: when a user logs fewer hours than their
  // effective contracted hours, only the logged share of their salary
  // lands on projects via Direct Labour. The remainder is the firm's
  // utilization shortfall — real cash paid that doesn't attribute to any
  // single project on its own. We compute it per user-month, sum to a
  // firm total for the KPI tile, and group by office-month so the
  // activity-overhead engine can allocate it to projects in that office
  // by hour share (same denominator as operational OH).
  //
  // gapCost(u, m) = proratedTrueCost(u, m) × max(0, normalized − totalLogged) / normalized
  // where proratedTrueCost has employer burden included and NO markup.
  // Admin-only because the Portfolio Aggregate itself is admin-only.
  // Computed before the engine so the engine can consume the office-month map.
  const { totalSalaryGap, salaryGapByOfficeMonth } = useMemo(() => {
    const empty = { totalSalaryGap: 0, salaryGapByOfficeMonth: {} };
    if (!isAdmin) return empty;
    if (!fullLedgerLoaded || !fullSalaryLedger) return empty;
    if (!activeUsers || activeUsers.length === 0) return empty;
    // The salary ledger is pre-populated months ahead (planned salaries).
    // Future months would otherwise produce a synthetic "gap" equal to the
    // full prorated salary (since no hours can be logged yet) and pull the
    // aggregate chart's timeline forward into empty space. Clamp to months
    // up to and including the current one.
    const now = new Date();
    const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    let total = 0;
    const byOfficeMonth = {};
    activeUsers.forEach(u => {
      const auth = u.authUserId;
      if (!auth) return;
      const userMonths = fullSalaryLedger[auth];
      if (!userMonths) return;
      const office = (u.officeLocation || 'NL') === 'IN' ? 'IN' : 'NL';
      Object.entries(userMonths).forEach(([ymKey, row]) => {
        if (ymKey > currentMonthKey) return;
        const baseSalary = Number(row?.base_salary) || 0;
        const bonus = Number(row?.bonus) || 0;
        if (baseSalary <= 0 && bonus <= 0) return;
        const m = ymKey.match(/^(\d{4})-(\d{1,2})$/);
        if (!m) return;
        const yr = parseInt(m[1], 10);
        const mo = parseInt(m[2], 10);
        const cost = calcMonthlyCost(u.id, yr, mo);
        const baseCH = cost.contractedHours || 176;
        const proratedCost = (cost.totalCost || 0) * baseCH / 176;
        if (proratedCost <= 0) return;
        const totalLogged = byUserMonth[u.id]?.[ymKey] || 0;
        const effCH = getEffectiveContractedHours
          ? getEffectiveContractedHours(u.id, yr, mo)
          : baseCH;
        const normalized = Math.max(totalLogged, effCH);
        if (normalized <= 0) return;
        const gap = proratedCost * Math.max(0, normalized - totalLogged) / normalized;
        if (gap > 0) {
          total += gap;
          if (!byOfficeMonth[office]) byOfficeMonth[office] = {};
          byOfficeMonth[office][ymKey] = (byOfficeMonth[office][ymKey] || 0) + gap;
        }
      });
    });
    return { totalSalaryGap: total, salaryGapByOfficeMonth: byOfficeMonth };
  }, [isAdmin, fullLedgerLoaded, fullSalaryLedger, activeUsers, byUserMonth, calcMonthlyCost, getEffectiveContractedHours]);

  // Activity-based overhead engine — shared with Project Cost Ledger via
  // src/utils/activityOverheadEngine.js. Both pages call computeActivityOverheadEngine
  // with the same inputs and therefore cannot drift on dormancy or operational OH.
  // The salary gap office-month map is also passed so the engine attributes the
  // firm's unattributed labour to projects by hour share, mirroring op OH.
  const activityOverheadEngine = useMemo(
    () => computeActivityOverheadEngine({
      activeTimeEntries, activeUsers, activeProjects, internalProjectIds,
      monthlyOHIndex, yearlyOHIndex,
      monthlyOfficeOverheads, yearlyOfficeOverheads: activeOfficeOverheads,
      DORMANCY_POOL_PCT, buildDebug: true,
      salaryGapByOfficeMonth,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTimeEntries, activeUsers, activeProjects, activeClients, activeOfficeOverheads,
     monthlyOfficeOverheads, monthlyOHIndex, yearlyOHIndex, projectsWithAnyTime,
     internalProjectIds, DORMANCY_POOL_PCT, salaryGapByOfficeMonth]
  );

  // Convenience aliases for chartData lookups
  const activityOverheadByProjectMonth = activityOverheadEngine.byProjectMonth;
  const dormancyByProjectMonth = activityOverheadEngine.dormancyByProjectMonth;
  const salaryGapByProjectMonth = activityOverheadEngine.salaryGapByProjectMonth;

  // ──────────────────────────────────────────────────────
  // Internal project cost distribution
  // ──────────────────────────────────────────────────────
  // Labour cost from internal projects (client = BRIO) is pooled per
  // office-month and distributed to billable projects by hour share.
  // This makes "BRIO General" etc. function as operating overhead.
  // ──────────────────────────────────────────────────────
  // Internal project cost distribution
  // ──────────────────────────────────────────────────────
  // Labour cost from internal projects (client = BRIO) is pooled per
  // office-month and distributed to billable projects by hour share.
  // Computed directly from time entry indices + calcMonthlyCost to
  // avoid the expensive getProjectLabourForMonth call cascade.
  const internalCostByProjectMonth = useMemo(() => computeInternalCostRedistribution({
    byProject,
    byUserMonth,
    activeUsers,
    internalProjectIds,
    billableOfficeHours: activityOverheadEngine.billableOfficeHours,
    projOfficeHours: activityOverheadEngine.projOfficeHours,
    getUserProfileAtDate,
    getEffectiveContractedHours,
    calcMonthlyCost,
  }), [internalProjectIds, activityOverheadEngine, byProject, byUserMonth,
       activeUsers, getUserProfileAtDate, getEffectiveContractedHours, calcMonthlyCost]);

  // ──────────────────────────────────────────────────────
  // INDEX: Paid invoices by (projectId, yearMonth)
  // Use paid_date month for timing of income
  //
  // Builds three parallel maps:
  //   - incomeByProjectMonth     : total income (NL + IN)
  //   - incomeNLByProjectMonth   : EUR-currency invoices (Rotterdam office)
  //   - incomeINByProjectMonth   : INR-currency invoices (Mumbai office)
  // The bar chart stacks NL and IN income; the area chart and totals
  // continue to use the combined map.
  // ──────────────────────────────────────────────────────
  const { incomeByProjectMonth, incomeNLByProjectMonth, incomeINByProjectMonth } = useMemo(() => {
    const all = {};
    const nl = {};
    const ix = {};
    allInvoices.forEach(inv => {
      // Include all non-draft invoices as recognised revenue (issued + paid).
      // NL/EUR invoices typically remain in 'issued' status without bank-confirmation,
      // so requiring paidConfirmed would incorrectly zero-out all NL income.
      if (inv.status === 'draft') return;
      const dateField = inv.paidDate || inv.invoiceDate;
      if (!dateField) return;
      const ymKey = dateField.substring(0, 7);
      const pid = inv.projectId;
      // EUR invoices store base_amount in EUR; convert to INR (base currency) so
      // that toDisplay() can then normalise to the chosen display currency correctly.
      // Use the pre-computed INR amount from v_invoices_reporting when available,
      // otherwise fall back to a live FX conversion via settings.
      let amountInBaseCurrency;
      if (inv.currency === 'EUR') {
        if (inv.baseAmountInrDefault != null) {
          amountInBaseCurrency = inv.baseAmountInrDefault;
        } else {
          const fxDate = `${ymKey}-01`;
          const rate = settingsHook?.getFxRateForDate?.(fxDate) ?? 0;
          amountInBaseCurrency = rate > 0 ? inv.baseAmount * rate : inv.baseAmount;
        }
      } else {
        amountInBaseCurrency = inv.baseAmount;
      }
      const target = inv.currency === 'EUR' ? nl : ix;
      if (!all[pid]) all[pid] = {};
      if (!target[pid]) target[pid] = {};
      all[pid][ymKey] = (all[pid][ymKey] || 0) + amountInBaseCurrency;
      target[pid][ymKey] = (target[pid][ymKey] || 0) + amountInBaseCurrency;
    });
    return { incomeByProjectMonth: all, incomeNLByProjectMonth: nl, incomeINByProjectMonth: ix };
  }, [allInvoices, settingsHook]);

  // ──────────────────────────────────────────────────────
  // HELPER: Monthly salary cost with markup
  // ──────────────────────────────────────────────────────
  // Delegate to centralised calcMonthlyCost (App.js) — matches SalaryCostPage
  const getMonthlyCostWithMarkup = useCallback((userId, yearMonth) => {
    const numId = toNumericId(userId);
    const p = parseYM(yearMonth);
    if (!p) return { totalCost: 0 };
    const cost = calcMonthlyCost(numId, p.year, p.month);
    return { totalCost: cost.totalCost };
  }, [calcMonthlyCost]);

  // Labour now flows through the shared computeProjectLabour utility —
  // see ../../utils/projectLabour.js. The previous getProjectLabourForMonth
  // helper has been removed; consumers call the utility directly so
  // Profitability and Cost Ledger can no longer drift on labour totals.

  // ──────────────────────────────────────────────────────
  // HELPER: Get months for a project (total mode)
  // Range: first time entry or first paid invoice (whichever earlier)
  //    to: last time entry or last paid invoice (whichever later)
  // ──────────────────────────────────────────────────────
  // getProjectMonths is shared with Project Cost Ledger via
  // src/utils/activityOverheadEngine.js. Both pages compute the same
  // active-period window so dormancy attribution and the per-project
  // monthly breakdown table cannot drift.
  const getProjectMonths = useCallback((project) => computeProjectMonths(project, {
    byProject, incomeByProjectMonth, periodType, selectedMonths,
  }), [periodType, selectedMonths, byProject, incomeByProjectMonth]);

  // ──────────────────────────────────────────────────────
  // COMPUTE: Per-project profitability data
  // ──────────────────────────────────────────────────────
  const projectDataMap = useMemo(() => {
    const map = {};
    const projects = statusFilter === 'all' || statusFilter === 'default'
      ? visibleProjects
      : visibleProjects.filter(p => p.status === statusFilter);

    projects.forEach(project => {
      const months = getProjectMonths(project);

      // Labour breakdown computed once per project via the shared engine.
      // Project Cost Ledger calls the same engine — totals are guaranteed
      // to agree because they come from one piece of code.
      const labourBreakdown = computeProjectLabour({
        byProject, byUserMonth, projectId: project.id, months,
        getUserProfileAtDate, getEffectiveContractedHours, calcMonthlyCost,
      });

      // Per-month data converted to display currency using monthly FX rates
      const chartData = months.map(ymKey => {
        const labour = labourBreakdown.byMonth[ymKey] || 0;
        const grossExpenses = expenseByProjectMonth[project.id]?.[ymKey] || 0;
        const clientReimbursed = clientDeductionByProjectMonth[project.id]?.[ymKey] || 0;
        const expenses = Math.max(0, grossExpenses - clientReimbursed);
        const directCostBase = labour + expenses;
        // Activity overhead allocator: operational pool + dormancy carrying cost
        const operationalOHBase = activityOverheadByProjectMonth[project.id]?.[ymKey] || 0;
        const dormancyCostBase = dormancyByProjectMonth[project.id]?.[ymKey] || 0;
        // Internal project costs distributed as overhead (BRIO General, etc.)
        const internalOHBase = internalCostByProjectMonth[project.id]?.[ymKey] || 0;
        // Salary gap attributed by the engine (hour share within office-month)
        const salaryGapBase = salaryGapByProjectMonth[project.id]?.[ymKey] || 0;
        const costBase = directCostBase + operationalOHBase + dormancyCostBase + internalOHBase + salaryGapBase;
        const incomeBase = incomeByProjectMonth[project.id]?.[ymKey] || 0;
        const incomeNLBase = incomeNLByProjectMonth[project.id]?.[ymKey] || 0;
        const incomeINBase = incomeINByProjectMonth[project.id]?.[ymKey] || 0;
        const fxDate = `${ymKey}-01`;
        return {
          month: shortMonth(ymKey),
          monthKey: ymKey,
          cost: toDisplay(costBase, fxDate),
          directCost: toDisplay(directCostBase, fxDate),
          overhead: toDisplay(operationalOHBase, fxDate),
          dormancy: toDisplay(dormancyCostBase, fxDate),
          internalOH: toDisplay(internalOHBase, fxDate),
          salaryGap: toDisplay(salaryGapBase, fxDate),
          income: toDisplay(incomeBase, fxDate),
          incomeNL: toDisplay(incomeNLBase, fxDate),
          incomeIN: toDisplay(incomeINBase, fxDate),
        };
      });

      // Totals in display currency (summed from converted monthly values)
      const totalExpenses = chartData.reduce((s, d) => s + d.cost, 0);
      const totalDirectCost = chartData.reduce((s, d) => s + d.directCost, 0);
      const totalOverhead = chartData.reduce((s, d) => s + d.overhead, 0);
      const totalDormancy = chartData.reduce((s, d) => s + d.dormancy, 0);
      const totalInternalOH = chartData.reduce((s, d) => s + d.internalOH, 0);
      const totalSalaryGap = chartData.reduce((s, d) => s + (d.salaryGap || 0), 0);
      const totalIncome = chartData.reduce((s, d) => s + d.income, 0);

      // Cumulative data for area chart (already in display currency)
      let cumCost = 0;
      let cumDirectCost = 0;
      let cumIncome = 0;
      const cumulativeData = chartData.map(d => {
        cumCost += d.cost;
        cumDirectCost += d.directCost;
        cumIncome += d.income;
        // Essential profit target band: expenses + profit margin threshold
        const cumCostWithTarget = cumCost * (1 + essentialProfitPct / 100);
        return {
          ...d,
          cumCost,
          cumDirectCost,
          cumIncome,
          cumProfit: cumIncome - cumCost,
          cumCostWithTarget,
          cumTargetProfit: cumCost * essentialProfitPct / 100,
        };
      });

      map[project.id] = {
        months,
        chartData,
        cumulativeData,
        totalExpenses,
        totalDirectCost,
        totalOverhead,
        totalDormancy,
        totalInternalOH,
        totalSalaryGap,
        totalIncome,
        profit: totalIncome - totalExpenses,
        isInternal: internalProjectIds.has(toNumericId(project.id)),
      };
    });
    return map;
  }, [visibleProjects, statusFilter, getProjectMonths, byProject, byUserMonth, getUserProfileAtDate, getEffectiveContractedHours, calcMonthlyCost, expenseByProjectMonth, clientDeductionByProjectMonth, incomeByProjectMonth, incomeNLByProjectMonth, incomeINByProjectMonth, activityOverheadByProjectMonth, dormancyByProjectMonth, internalCostByProjectMonth, salaryGapByProjectMonth, internalProjectIds, toDisplay, essentialProfitPct]);

  // Cache write: once the prerequisites for the loaded-cost stack are
  // loaded, push per-project totals to project_loaded_costs. Throttled
  // to one write per mount + per significant input change. The Inquiries
  // Fee Intelligence drawer reads from that cache instead of recomputing.
  const lastCacheWriteRef = React.useRef(0);
  useEffect(() => {
    if (typeof refreshProjectLoadedCosts !== 'function') return;
    if (!allTimeEntriesLoaded || !fullLedgerLoaded) return;
    const now = Date.now();
    // Throttle: at most one write every 30 s. Inputs that move the
    // numbers (time entries, ledger, expenses) trigger the effect via
    // dep array, but rapid sequential renders shouldn't spam the RPC.
    if (now - lastCacheWriteRef.current < 30000) return;
    lastCacheWriteRef.current = now;
    refreshProjectLoadedCosts().catch(err => {
      console.warn('[Profitability] cache write skipped:', err?.message || err);
    });
    // refreshProjectLoadedCosts closes over the latest inputs; we only
    // re-trigger when load-state flips or the visible portfolio changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTimeEntriesLoaded, fullLedgerLoaded, activeTimeEntries, activeExpenses, refreshProjectLoadedCosts]);

  // ──────────────────────────────────────────────────────
  // SORTED PROJECTS: by total expenses descending
  // ──────────────────────────────────────────────────────
  const sortedProjects = useMemo(() => {
    const projects = statusFilter === 'all' || statusFilter === 'default'
      ? visibleProjects
      : visibleProjects.filter(p => p.status === statusFilter);

    return [...projects]
      .filter(p => {
        const d = projectDataMap[p.id];
        return d && (d.totalExpenses > 0 || d.totalIncome > 0);
      })
      .sort((a, b) => {
        const da = projectDataMap[a.id]?.totalExpenses || 0;
        const db = projectDataMap[b.id]?.totalExpenses || 0;
        if (da !== db) return db - da;
        return (a.projectCode || a.name || '').localeCompare(b.projectCode || b.name || '');
      });
  }, [visibleProjects, statusFilter, projectDataMap]);

  // ──────────────────────────────────────────────────────
  // BILLED / NON-BILLED SPLIT
  // ──────────────────────────────────────────────────────
  const billedProjects = useMemo(
    () => sortedProjects.filter(p => !internalProjectIds.has(toNumericId(p.id))),
    [sortedProjects, internalProjectIds]
  );
  const nonBilledProjects = useMemo(
    () => sortedProjects.filter(p => internalProjectIds.has(toNumericId(p.id))),
    [sortedProjects, internalProjectIds]
  );

  // ──────────────────────────────────────────────────────
  // GRAND TOTALS
  // ──────────────────────────────────────────────────────
  // Exclude internal projects (BRIO General etc.) from the portfolio
  // aggregate — their labour is already represented on every billable
  // project's row as Internal OH, so including them would double-count.
  // The per-project list below the aggregate always shows all projects.
  const aggregateProjects = useMemo(
    () => sortedProjects.filter(p => !internalProjectIds.has(toNumericId(p.id))),
    [sortedProjects, internalProjectIds]
  );

  // Convert the firm-wide gap to display currency for the KPI tile.
  // The gap accrues across many months so a single FX point is approximate;
  // use the most recent month's rate. The per-project gap stripe in chart
  // data is FX-converted month-by-month so this only affects the headline.
  const totalSalaryGapDisplay = useMemo(() => {
    if (!totalSalaryGap) return 0;
    const today = new Date();
    return toDisplay(totalSalaryGap, `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`);
  }, [totalSalaryGap, toDisplay]);

  const grandTotals = useMemo(() => {
    let projectExpenses = 0, income = 0, attributedGap = 0;
    aggregateProjects.forEach(p => {
      const d = projectDataMap[p.id];
      if (d) {
        projectExpenses += d.totalExpenses; // includes per-project attributed salary gap
        income += d.totalIncome;
        attributedGap += d.totalSalaryGap || 0;
      }
    });
    // Residual: any portion of the firm-wide gap that the engine couldn't
    // attribute (office-month with gap pool but zero billable hours). Add
    // it on top so the KPI tile still equals firm cash outflow even if
    // attribution leaves a remainder.
    const residual = Math.max(0, totalSalaryGapDisplay - attributedGap);
    const expenses = projectExpenses + residual;
    return {
      expenses,
      income,
      profit: income - expenses,
      projectExpenses,
      salaryGap: totalSalaryGapDisplay,
      salaryGapResidual: residual,
    };
  }, [aggregateProjects, projectDataMap, totalSalaryGapDisplay]);

  // ──────────────────────────────────────────────────────
  // YEAR OPTIONS
  // ──────────────────────────────────────────────────────
  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = 2018; y <= currentYear + 1; y++) years.push(y);
    return years;
  }, [currentYear]);

  // ──────────────────────────────────────────────────────
  // AGGREGATE: Time-based cumulative (admin only)
  // ──────────────────────────────────────────────────────
  const aggregateTimeData = useMemo(() => {
    if (!isAdmin || aggregateProjects.length === 0) return [];
    // Per-project chartData already includes the engine-attributed salary
    // gap, so the cumulative sum naturally equals the firm cash outflow
    // and matches the KPI tile (modulo any unallocated residual, surfaced
    // separately in grandTotals).
    return buildAggregateByTime(
      projectDataMap,
      aggregateProjects.map(p => p.id),
      essentialProfitPct,
    );
  }, [isAdmin, aggregateProjects, projectDataMap, essentialProfitPct]);

  // ──────────────────────────────────────────────────────
  // BAR CHART DATA (period-bucketed; monthly / quarterly / weekly)
  //
  // Bucket cadence is picked by selectBarBucket():
  //   FY / CY                       → monthly
  //   Custom < 3mo, Mo              → weekly
  //   3–23 months                   → monthly
  //   ≥ 24 months, Total ≥ 24mo     → quarterly
  // ──────────────────────────────────────────────────────
  const aggregateBarData = useMemo(() => {
    if (!isAdmin || aggregateProjects.length === 0) return { rows: [], bucket: 'monthly' };
    // Salary gap is now attributed per-project by the engine and folded
    // into chartData, so summing per-project monthly entries naturally
    // produces the correct aggregate including the gap stripe.
    const monthly = buildBarAggregateByMonth(projectDataMap, aggregateProjects.map(p => p.id));
    const monthCount = periodType === 'total' ? monthly.length : selectedMonths.length;
    let bucket;
    if (aggregateBarMode === 'fy') bucket = 'fyYearly';
    else if (aggregateBarMode === 'cy') bucket = 'yearly';
    else bucket = selectBarBucket(periodType, monthCount);
    return { rows: bucketChartData(monthly, bucket), bucket };
  }, [isAdmin, aggregateProjects, projectDataMap, periodType, selectedMonths, aggregateBarMode]);

  const projectBarDataMap = useMemo(() => {
    const map = {};
    Object.keys(projectDataMap).forEach(pid => {
      const d = projectDataMap[pid];
      if (!d || !Array.isArray(d.chartData)) return;
      const monthCount = periodType === 'total' ? d.chartData.length : selectedMonths.length;
      const bucket = selectBarBucket(periodType, monthCount);
      map[pid] = { rows: bucketChartData(d.chartData, bucket), bucket };
    });
    return map;
  }, [projectDataMap, periodType, selectedMonths]);

  // ──────────────────────────────────────────────────────
  // INVOICE INDEX (per project) — used as a fallback source of per-stage
  // dates for both the per-project stage markers and the Stage aggregate
  // view, for projects whose stageTimeline has no dates populated.
  // ──────────────────────────────────────────────────────
  const invoicesByProjectForStages = useMemo(() => {
    const idx = {};
    allInvoices.forEach(inv => {
      const pid = inv.projectId;
      if (pid == null) return;
      if (!idx[pid]) idx[pid] = [];
      idx[pid].push(inv);
    });
    return idx;
  }, [allInvoices]);

  // ──────────────────────────────────────────────────────
  // AGGREGATE: Stage-based cumulative (admin only)
  // ──────────────────────────────────────────────────────
  const aggregateStageData = useMemo(() => {
    if (!isAdmin || aggregateProjects.length === 0) return [];
    return buildAggregateByStage(aggregateProjects, projectDataMap, essentialProfitPct, invoicesByProjectForStages);
  }, [isAdmin, aggregateProjects, projectDataMap, essentialProfitPct, invoicesByProjectForStages]);

  // ──────────────────────────────────────────────────────
  // STAGE MARKERS: Per-project transition markers
  // ──────────────────────────────────────────────────────
  const stageMarkersMap = useMemo(() => {
    const map = {};
    sortedProjects.forEach(p => {
      map[p.id] = extractStageMarkersHelper(p, invoicesByProjectForStages[p.id] || []);
    });
    return map;
  }, [sortedProjects, invoicesByProjectForStages]);

  // ──────────────────────────────────────────────────────
  // SHARED DOMAIN: Common Y-axis across all visible charts
  // ──────────────────────────────────────────────────────
  const sharedYDomain = useMemo(() => {
    if (scaleMode !== 'shared') return null;
    return computeSharedDomain(projectDataMap, sortedProjects.map(p => p.id), chartMode);
  }, [scaleMode, projectDataMap, sortedProjects, chartMode]);

  // ──────────────────────────────────────────────────────
  // MULTI-SELECT: Selected projects + derived chart data
  // ──────────────────────────────────────────────────────
  const selectedProjects = useMemo(() => {
    return sortedProjects.filter(p => selectedProjectIds[p.id]);
  }, [sortedProjects, selectedProjectIds]);

  const selectedCombinedData = useMemo(() => {
    if (multiProjectMode !== 'combined' || selectedProjects.length === 0) return [];
    return buildAggregateByTime(projectDataMap, selectedProjects.map(p => p.id), essentialProfitPct);
  }, [multiProjectMode, selectedProjects, projectDataMap, essentialProfitPct]);

  const selectedCompareData = useMemo(() => {
    if (multiProjectMode !== 'compare' || selectedProjects.length === 0) return [];
    return mergeProjectProfitLines(selectedProjects, projectDataMap);
  }, [multiProjectMode, selectedProjects, projectDataMap]);

  const toggleProjectSelection = useCallback((pid) => {
    setSelectedProjectIds(prev => {
      const next = { ...prev };
      if (next[pid]) delete next[pid];
      else next[pid] = true;
      return next;
    });
  }, []);

  // ──────────────────────────────────────────────────────
  // TOGGLE (expand/collapse)
  // ──────────────────────────────────────────────────────
  const toggleProject = (pid) => {
    setExpandedProjects(prev => ({ ...prev, [pid]: !prev[pid] }));
  };
  const toggleLedger = (pid) => {
    setExpandedLedgers(prev => ({ ...prev, [pid]: !prev[pid] }));
  };

  // ════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════

  return (
    <div className="brio-analytics-font" style={{ maxWidth: 960, margin: '0 auto', position: 'relative' }}>
      {/* Unified loading progress bar — visible while ANY data source is still loading.
          Width tracks time-entry progress (the longest fetch); pinned at 99% if time
          entries finish first but invoices/overheads/ledger are still resolving. */}
      {dataLoading && (
        <div style={{ position: 'sticky', top: 0, zIndex: 10, width: '100%', height: 3, background: 'var(--brio-grey-light, #e5e5e5)', overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${overallProgress}%`,
            background: 'var(--brio-gold, #9F902A)',
            transition: 'width 0.3s ease-out',
          }} />
        </div>
      )}
      <BrioFrame>
        <div style={{ paddingTop: 'var(--brio-space-m)', paddingBottom: 'var(--brio-space-m)' }}>

        {/* ── Title + Period selector ── */}
        <BrioAnalyticsSurface padding="m">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <h3 className="brio-section-header__title" style={{ margin: 0 }}>
              project profitability
            </h3>
            <div style={{ fontSize: 12, color: 'var(--brio-text-secondary)', fontWeight: 300 }}>
              {periodLabel} · {dataLoading
                ? `loading… ${overallProgress}%`
                : `${sortedProjects.length} project${sortedProjects.length !== 1 ? 's' : ''}`}
            </div>
          </div>

          {/* All controls in one row */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {[
              { id: 'month', label: 'Mo' },
              { id: 'calendarYear', label: 'CY' },
              { id: 'indianFY', label: 'FY' },
              { id: 'total', label: 'Total' },
              { id: 'custom', label: 'Custom' },
            ].map(pt => (
              <button
                key={pt.id}
                onClick={() => setPeriodType(pt.id)}
                className={`brio-btn ${periodType === pt.id ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                style={{ fontSize: 10, padding: '3px 8px' }}
              >
                {pt.label}
              </button>
            ))}
            {periodType === 'month' && (
              <>
                <select value={selMonth} onChange={e => setSelMonth(parseInt(e.target.value))}
                  className="brio-modal-select" style={{ width: 'auto', fontSize: 10, padding: '2px 24px 2px 6px', height: 22 }}>
                  {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
                <select value={selYear} onChange={e => setSelYear(parseInt(e.target.value))}
                  className="brio-modal-select" style={{ width: 'auto', fontSize: 10, padding: '2px 24px 2px 6px', height: 22 }}>
                  {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </>
            )}
            {periodType === 'calendarYear' && (
              <select value={cyYear} onChange={e => setCyYear(parseInt(e.target.value))}
                className="brio-modal-select" style={{ width: 'auto', fontSize: 10, padding: '2px 24px 2px 6px', height: 22 }}>
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            {periodType === 'indianFY' && (
              <select value={fyYear} onChange={e => setFyYear(parseInt(e.target.value))}
                className="brio-modal-select" style={{ width: 'auto', fontSize: 10, padding: '2px 24px 2px 6px', height: 22 }}>
                {yearOptions.map(y => <option key={y} value={y}>FY{String(y).slice(2)}–{String(y + 1).slice(2)}</option>)}
              </select>
            )}
            {periodType === 'custom' && (
              <>
                <BrioMonthPicker value={customStart} onChange={v => setCustomStart(v)}
                  style={{ width: 'auto', fontSize: 10 }} clearable={false} className="brio-datepicker--compact"
                  formatDisplay={v => shortMonth(v) || 'Start'} />
                <span style={{ fontSize: 10, color: 'var(--brio-text-secondary)' }}>–</span>
                <BrioMonthPicker value={customEnd} onChange={v => setCustomEnd(v)}
                  style={{ width: 'auto', fontSize: 10 }} clearable={false} className="brio-datepicker--compact"
                  formatDisplay={v => shortMonth(v) || 'End'} />
              </>
            )}
            <div style={{ width: 1, height: 14, background: brioColors.border, margin: '0 2px' }} />

            {/* Slide toggle: Chart view (Financial / Performance / Bar) */}
            <div style={{ display: 'inline-flex', borderRadius: 3, overflow: 'hidden', border: `1px solid ${brioColors.border}` }}>
              {[
                { id: 'financial', label: 'Financial' },
                { id: 'performance', label: 'Performance' },
                { id: 'bar', label: 'Bar' },
              ].map(cv => {
                const active = cv.id === 'bar' ? chartType === 'bar' : chartType === 'area' && chartMode === cv.id;
                return (
                  <button key={cv.id} onClick={() => {
                    if (cv.id === 'bar') { setChartType('bar'); }
                    else { setChartType('area'); setChartMode(cv.id); }
                  }}
                    style={{ fontSize: 10, padding: '3px 6px', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                      background: active ? brioColors.gold : 'transparent',
                      color: active ? '#fff' : brioColors.grey }}>
                    {cv.label}
                  </button>
                );
              })}
            </div>

            {/* Slide toggle: Scale */}
            <div style={{ display: 'inline-flex', borderRadius: 3, overflow: 'hidden', border: `1px solid ${brioColors.border}` }}>
              {[{ id: 'local', label: 'Abs.' }, { id: 'shared', label: 'Rel.' }].map(sm => (
                <button key={sm.id} onClick={() => setScaleMode(sm.id)}
                  style={{ fontSize: 10, padding: '3px 6px', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                    background: scaleMode === sm.id ? brioColors.gold : 'transparent',
                    color: scaleMode === sm.id ? '#fff' : brioColors.grey }}>
                  {sm.label}
                </button>
              ))}
            </div>

            <div style={{ flex: 1 }} />
            {sortedProjects.length > 0 && (
              <button
                onClick={() => {
                  const allExpanded = sortedProjects.every(p => expandedProjects[p.id]);
                  if (allExpanded) {
                    setExpandedProjects({});
                    setExpandedLedgers({});
                  } else {
                    const allIds = {};
                    sortedProjects.forEach(p => { allIds[p.id] = true; });
                    setExpandedProjects(allIds);
                  }
                }}
                className="brio-btn brio-btn-secondary"
                style={{ fontSize: 10, padding: '3px 8px' }}
              >
                {sortedProjects.every(p => expandedProjects[p.id]) ? 'Collapse All' : 'Expand All'}
              </button>
            )}
          </div>

          {/* Description of current toggle selections */}
          <div style={{ fontSize: 9, color: brioColors.grey, fontWeight: brioStyles.fontWeightLight, marginTop: 6 }}>
            Overhead allocated by project hour share per office-month
            {' · '}
            {chartType === 'bar'
              ? 'Per-bucket grouped bars: debits left, income right'
              : (chartMode === 'financial' ? 'Stacked cost, target and income areas' : 'Costs below zero, income above zero')}
            {' · '}
            {scaleMode === 'local' ? 'Each chart scaled independently' : 'All charts share Y-axis'}
          </div>
        </BrioAnalyticsSurface>

        {/* ── Methodology explainer ── */}
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={() => setShowMethodology(prev => !prev)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 11,
              color: brioColors.grey,
              fontFamily: brioStyles.fontFamily,
              fontWeight: brioStyles.fontWeightRegular,
              padding: '2px 0',
            }}
          >
            {showMethodology
              ? <ChevronDown size={12} color={brioColors.grey} />
              : <ChevronRight size={12} color={brioColors.grey} />
            }
            How profitability is calculated
          </button>
        </div>
        {showMethodology && (
          <BrioAnalyticsSurface padding="m" style={{ marginTop: 4 }}>
            <div style={{ fontSize: 11, lineHeight: 1.7, color: brioColors.black, fontWeight: brioStyles.fontWeightRegular }}>
              <p style={{ margin: '0 0 10px', color: brioColors.grey }}>
                This page shows project profitability by combining income, direct cost, and overhead allocation over time.
              </p>

              <p style={{ margin: '12px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>1. Income</p>
              <p style={{ margin: '0 0 6px' }}>
                Income is the <strong>base amount</strong> of every non-draft invoice linked to the project,
                bucketed in the month the invoice was <strong>paid</strong>. If a paid date is not yet recorded,
                the invoice falls back to its issue date so unpaid (issued) invoices still appear.
              </p>
              <p style={{ margin: 0, color: brioColors.grey }}>
                Note: GST and TDS are <em>not</em> treated as income. GST collected on a sale is a pass-through
                liability owed to the tax authority (it can be partly offset by GST paid on expenses, but that
                offset is an accounting flow, not project revenue). TDS withheld at source is a prepayment of
                income tax on the project owner&rsquo;s behalf, not an income reduction. Both therefore live
                outside the profitability picture and only the pre-tax base amount is recognised here.
              </p>

              <p style={{ margin: '12px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>1a. Design Fee (header only)</p>
              <p style={{ margin: '0 0 6px' }}>
                The <strong>Fee</strong> figure shown in gold next to each project header is the total
                contracted billing potential — informational only; it does <em>not</em> feed into
                Income or Loaded Cost. It is the sum of:
              </p>
              <ul style={{ margin: '0 0 6px', paddingLeft: 20 }}>
                <li><strong>Base Design Fee</strong> (the project&rsquo;s <code>budget</code> field).</li>
                <li>
                  <strong>CC Retainer estimate</strong> = <code>monthlyAmount × totalMonths</code> from
                  the project dialog. The configured month count is always used, regardless of whether
                  the retainer is currently active or paused, so the figure represents the full
                  contracted scope rather than what is currently being collected.
                </li>
                <li><strong>Misc Design Fee scopes</strong> (any ad-hoc scopes added to the project).</li>
              </ul>
              <p style={{ margin: '0 0 6px' }}>
                All three are entered in the project&rsquo;s contract currency and converted to the
                display currency at the current month&rsquo;s FX rate.
              </p>
              <p style={{ margin: 0 }}>
                On the per-project chart the same number is drawn as a dashed gold horizontal line when
                it fits within ~1.5&times; of the chart&rsquo;s natural Y-max; otherwise a small{' '}
                <em>&uarr; Fee</em> indicator is pinned to the top-right of the plot so the cost / income
                signal keeps its original scale. The gap between Income and Design Fee is the unbilled portion
                of the contract.
              </p>

              <p style={{ margin: '12px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>2. Direct Cost</p>
              <p style={{ margin: '0 0 6px' }}>
                Direct cost has two components: <strong>labour cost</strong> and <strong>project-specific expenses</strong>.
              </p>
              <p style={{ margin: '0 0 6px' }}>
                Labour cost uses the firm's <strong>true cash cost</strong> per employee per month —
                base salary + bonus + employer burden where applicable (Dutch pension and
                Loonbelasting for NL non-director employees, pro-rated by salary share within each
                quarter). The salary markup factor is <em>not</em> applied here: markup is a
                target-revenue multiplier used by the Salary Cost report, and applying it on the
                cost side would double-count overhead that is already allocated separately as
                Op. Overhead (§3a).
              </p>
              <p style={{ margin: '0 0 6px' }}>
                That true monthly cost is prorated for contracted hours and allocated to this
                project based on its share of the employee's total logged hours that month.
              </p>
              <p style={{ margin: '0 0 6px' }}>
                Project-specific expenses (travel, materials, subconsultants, etc.) are added on top
                of labour cost in the month they were incurred.
              </p>
              <p style={{ margin: '8px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>What happens to un-logged hours?</p>
              <p style={{ margin: '0 0 6px' }}>
                The denominator used to spread an employee's monthly cost across projects is{' '}
                <code>max(total logged hours, effective contracted hours)</code>, where effective
                contracted hours = contracted hours minus public holidays and approved leave.
                Three cases:
              </p>
              <ul style={{ margin: '0 0 6px', paddingLeft: 20 }}>
                <li>
                  <strong>Logged = effective contracted hours.</strong> The full monthly cost is
                  distributed across the projects worked on, proportional to hours.
                </li>
                <li>
                  <strong>Logged &gt; effective contracted hours (overtime).</strong> The
                  denominator becomes the logged total, so the full cost is still distributed
                  across projects — overtime hours dilute the per-hour rate rather than adding cost.
                </li>
                <li>
                  <strong>Logged &lt; effective contracted hours (utilization shortfall).</strong>{' '}
                  Only the logged share of the salary lands on projects. The remaining
                  <code> (effective contracted hours − total logged) / effective contracted hours </code>
                  of that month's cost is the <strong>cost of un-logged hours</strong>. It is{' '}
                  <strong>not charged to any single project</strong> (projects should not be
                  penalised for hours their team did not work on them) but it <strong>is</strong>{' '}
                  pooled firm-wide and added to the Portfolio Aggregate as the &ldquo;Salary Gap&rdquo;
                  line so the portfolio total matches true firm cash cost. Supervisors do not see
                  Portfolio Aggregate; the canonical surface for the gap is the firm-wide
                  Salary Cost report.
                </li>
              </ul>
              <p style={{ margin: 0 }}>
                Internal projects (BRIO General etc.) follow the same rule when they donate their
                labour into the Internal OH pool — only hours actually logged are pooled and
                redistributed.
              </p>

              <p style={{ margin: '12px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>3. Overhead</p>
              <p style={{ margin: '0 0 6px' }}>
                Overhead represents a share of office operating cost allocated to the project.
                The monthly breakdown splits it into two columns: <strong>Op. Overhead</strong>
                (the office's non-labour running cost) and <strong>Internal OH</strong>
                (labour spent on internal projects, redistributed to billable work).
              </p>

              <p style={{ margin: '8px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>3a. Op. Overhead (Operational Overhead)</p>
              <p style={{ margin: '0 0 6px' }}>
                Op. Overhead is each billable project's share of the office's <em>non-labour</em>
                operating cost (rent, utilities, office supplies, travel, professional services,
                insurance, etc.) for the month. <strong>Employer-burden categories</strong>{' '}
                (Pension and Salary Tax / Loonbelasting) <strong>are excluded from this pool</strong>{' '}
                — they're attributed to NL employees' Direct Labour instead, because they scale
                with each user's salary, not with office-wide hour-share.
              </p>
              <p style={{ margin: '0 0 6px' }}>
                The remaining pool is allocated activity-based:
              </p>
              <ul style={{ margin: '0 0 6px', paddingLeft: 20 }}>
                <li>overhead is allocated only in months where the project has recorded time entries</li>
                <li>the project receives only its share of that month's office overhead</li>
                <li>that share is based on the project's hours as a proportion of total allocable office hours in that month</li>
              </ul>
              <div style={{
                margin: '8px 0',
                padding: '8px 12px',
                background: brioColors.greyLight,
                borderRadius: 4,
                fontSize: 10,
                fontFamily: 'monospace',
                lineHeight: 1.8,
                whiteSpace: 'pre-line',
              }}>
{`Project Op. Overhead for Month
= Office Overhead Pool for Month
  × (Project Allocable Hours / Total Office Allocable Hours)

Where:
  Project Allocable Hours = worked hours logged to this project in that month
  Total Office Allocable Hours = worked hours logged across all projects in the same office in that month`}
              </div>
              <p style={{ margin: '6px 0 0' }}>
                This means: if no one worked on the project in a month, that month gets no operational overhead.
                If the project used a larger share of office time, it receives a larger share of overhead.
                Invoice-only months do not create overhead by themselves.
              </p>

              <p style={{ margin: '8px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>3b. Internal OH (Internal Overhead)</p>
              <p style={{ margin: '0 0 6px' }}>
                Internal OH is the labour cost from internal, non-billable projects
                (those with no client or with client &ldquo;BRIO&rdquo; — e.g. BRIO General, internal R&amp;D)
                redistributed onto billable projects. Time spent on internal work is real cost
                the firm carries, but it never produces an invoice, so the engine pools it and charges
                it back to the projects that are producing revenue, in proportion to each billable
                project's share of that office-month's billable hours.
              </p>
              <div style={{
                margin: '8px 0',
                padding: '8px 12px',
                background: brioColors.greyLight,
                borderRadius: 4,
                fontSize: 10,
                fontFamily: 'monospace',
                lineHeight: 1.8,
                whiteSpace: 'pre-line',
              }}>
{`Project Internal OH for Month
= Sum of internal-project labour cost in the office that month
  × (Project Billable Hours / Total Office Billable Hours)`}
              </div>
              <p style={{ margin: '6px 0 0' }}>
                Internal OH only appears on billable projects. Internal projects themselves do not
                receive Internal OH (their labour is the source of the pool, not a destination).
              </p>

              <p style={{ margin: '12px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>4. Dormancy Carrying Cost</p>
              <p style={{ margin: '0 0 6px' }}>
                A small portion ({(DORMANCY_POOL_PCT * 100).toFixed(0)}%) of each office-month overhead pool is reserved as a dormancy pool.
                This is distributed evenly across dormant open projects in that office-month.
              </p>
              <p style={{ margin: '0 0 6px' }}>
                A project is considered dormant when it is still open (not completed/archived),
                has zero allocable hours that month, but has evidence of activity in other months.
              </p>
              <p style={{ margin: 0 }}>
                Dormancy carrying cost is shown separately from operational overhead in the monthly breakdown.
              </p>

              <p style={{ margin: '12px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>5. Loaded Cost</p>
              <p style={{ margin: '0 0 6px' }}>Loaded Cost (per project) = Direct Cost (labour + expenses) + Op. Overhead + Dormancy Carrying Cost + Internal OH + Salary Gap</p>
              <p style={{ margin: '0 0 6px' }}>
                <strong>Salary Gap</strong> is the salary cost of contracted hours that nobody logged
                to a project that month — real cash paid that has no specific time entry. The
                activity-overhead engine attributes it to projects by hour share within each
                office-month (same denominator as Op. Overhead), so per-project Loaded Costs sum
                to the firm cash outflow without leaving an unattributed remainder. The hatched
                stripe in the bar chart marks this as engine-allocated rather than direct.
              </p>
              <p style={{ margin: '0 0 6px' }}>
                <strong>No markup factor</strong> is applied on this page. At the firm level
                (Portfolio Aggregate) Σ Loaded Cost &asymp; salaries paid + employer burden +
                non-labour overhead.
              </p>
              <p style={{ margin: 0, color: brioColors.grey }}>
                The <strong>Project Cost Ledger</strong> report uses the same methodology (true cash cost,
                no markup, employer burden in Direct Cost, Pension &amp; Salary Tax excluded from
                Op. Overhead) so its per-project Direct Cost and Op. Overhead match this report
                column-for-column. The <strong>Salary Cost</strong> report is the canonical
                user-month view: it shows base salary + bonus + a dedicated <em>Burden</em> column
                (each NL employee&rsquo;s pro-rata share of the quarterly Pension + Salary Tax
                pool), and reconciles to the Direct Labour totals on the other two reports. Markup
                is applied only on the Salary Cost report, where it represents a target-revenue
                multiplier, not a cost.
              </p>

              <p style={{ margin: '12px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>6. Profit</p>
              <p style={{ margin: 0 }}>Profit = Income − Loaded Cost</p>

              <p style={{ margin: '12px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>7. Monthly Breakdown</p>
              <p style={{ margin: 0 }}>
                The monthly table shows these values month by month so you can see when income was recognized,
                when salary cost was incurred, when overhead was allocated, and how profitability changed over
                the life of the project.
              </p>

              <p style={{ margin: '12px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>8. Overhead data source</p>
              <p style={{ margin: '0 0 4px' }}>
                Monthly overhead pools are sourced from the <code>office_overheads_monthly</code> table when available.
                If no monthly row exists for a given office-month, the engine falls back to yearly overhead / 12.
                Missing data is flagged in the reconciliation output.
              </p>

              <p style={{ margin: '12px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>9. Why some months may show no overhead</p>
              <p style={{ margin: '0 0 4px' }}>A month may show no operational overhead when:</p>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>the project had no allocable time entries in that month</li>
                <li>the office had no allocable hours in that month</li>
                <li>there was no valid office overhead pool available for that month</li>
                <li>the allocated amount is extremely small and rounds to zero in display</li>
              </ul>
              <p style={{ margin: '6px 0 0' }}>
                However, a dormant project may still receive a dormancy carrying cost even with zero hours.
              </p>

              <p style={{ margin: '12px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>10. Reconciliation</p>
              <p style={{ margin: 0 }}>
                Overhead allocation is auditable.
                For each office-month: Unallocated = Pool − Op. Allocated − Dormancy Allocated.
                The delta should be near zero except for rounding.
                Pool source (monthly vs yearly fallback vs missing) is shown per row.
              </p>

              <p style={{ margin: '12px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>11. Contracted hours assumptions for historical periods</p>
              <p style={{ margin: '0 0 6px' }}>
                Labour cost allocation uses <em>effective contracted hours</em> — the denominator that represents
                how much a person was actually expected to work in a given month, after deducting public holidays
                and annual leave.
              </p>
              <p style={{ margin: '0 0 6px' }}>
                For periods <strong>before January 2026</strong>, no public holiday records exist in the database.
                A standard estimate is used: <strong>11 days/year (NL)</strong> and <strong>12 days/year (IN)</strong>,
                spread evenly across 12 months.
              </p>
              <p style={{ margin: 0 }}>
                For periods <strong>before April 2026</strong>, the time-off request system was not yet in use.
                Annual leave is estimated using company policy: <strong>30 days/year (NL)</strong> and{' '}
                <strong>15 days/year (IN)</strong>, spread evenly across 12 months.
                From April 2026 onwards, actual approved leave records are used.
              </p>

              <p style={{ margin: '16px 0 4px', fontWeight: brioStyles.fontWeightMedium, borderTop: `1px solid ${brioColors.border}`, paddingTop: 12 }}>Chart Views</p>

              <p style={{ margin: '8px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>Financial Structure</p>
              <p style={{ margin: 0 }}>
                Shows cumulative areas stacked on top of each other: the profit target band (outermost, gold),
                loaded cost (direct cost + overhead), direct cost (labour + expenses), and income (green, superimposed on top).
                Where the income area rises above the loaded cost area, the project is profitable.
                Where it falls below, the project is running at a loss.
              </p>

              <p style={{ margin: '8px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>Performance</p>
              <p style={{ margin: 0 }}>
                Splits the chart at a zero line. Cumulative costs (loaded cost, direct cost, target) are shown as negative areas
                below zero. Cumulative income is shown as a positive area above zero. A gold profit line shows the net position
                across both sides. This makes it easy to see whether income is keeping pace with costs over time.
              </p>

              <p style={{ margin: '8px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>Scale: Absolute vs Relative</p>
              <p style={{ margin: 0 }}>
                <strong>Absolute</strong> gives each project chart its own Y-axis scale, optimized for readability.
                <strong> Relative</strong> forces all project charts to use the same Y-axis range, making it easy to
                visually compare the magnitude of costs and income across projects.
              </p>

              <p style={{ margin: '8px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>Overhead Model</p>
              <p style={{ margin: 0 }}>
                Overhead is allocated only in months with logged hours, proportional to the project&rsquo;s
                share of total office hours that month. This activity-based allocation reflects actual
                resource usage, which is consistently more accurate than spreading overhead evenly across
                a project&rsquo;s elapsed duration (the legacy calendar carrying-cost approach used before).
              </p>

              <p style={{ margin: '8px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>Chart time-axis</p>
              <p style={{ margin: 0 }}>
                Each project&rsquo;s chart spans from earliest activity (first time entry, first invoice,
                or stage date) to:
              </p>
              <ul style={{ margin: '4px 0 8px', paddingLeft: 20 }}>
                <li>
                  <strong>Today</strong> &mdash; for projects with status <code>active</code> (or missing
                  status), so the chart keeps showing dormancy carry-cost on live projects with no recent
                  activity.
                </li>
                <li>
                  <strong>Last activity month</strong> &mdash; for any non-active status (<code>completed</code>,
                  <code> on_hold</code>, <code>cancelled</code>, <code>archived</code>, &hellip;). The chart
                  stops where work and billing stopped instead of trailing flat to today.
                </li>
              </ul>

              <p style={{ margin: '8px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>Stage Markers</p>
              <p style={{ margin: '0 0 6px' }}>
                Thin grey vertical lines on each project chart mark stage transitions (ADVC, CD, SD, DD, AOC, TD, GFC, CMPL, CC).
                AD and ID phases are combined into a single stage marker (e.g. CD = the later of CD-AD and CD-ID).
                GFC1, GFC2, GFC3 are combined into a single GFC marker. CC uses the earliest CC invoice date.
              </p>
              <p style={{ margin: 0 }}>
                Date sourcing happens in two passes. <strong>Primary:</strong> each project&rsquo;s{' '}
                <code>stage_timeline</code> &mdash; <code>actualDate</code>, <code>invoiceDate</code>, or
                <code> paidDate</code> in that priority, filled in via the Timeline page. <strong>Fallback:</strong>{' '}
                if those fields are not populated (typical for older projects or invoices raised outside
                the Timeline workflow), the engine looks at the project&rsquo;s non-draft invoices and
                matches each combined stage against invoice <code>stage_key</code> values, including
                part-stage forms like <code>cd_ad__part__&lt;id&gt;</code> and monthly CC sub-keys like{' '}
                <code>cc__YYYY-MM</code>. So a project gets markers as long as it has raised invoices
                tagged to those stages, even without any <code>stage_timeline</code> dates.
              </p>

              {isAdmin && (
                <>
                  <p style={{ margin: '8px 0 4px', fontWeight: brioStyles.fontWeightMedium }}>Portfolio Aggregate</p>
                  <p style={{ margin: '0 0 6px' }}>
                    When turned on, aggregates all visible projects into a single chart. Loading all time
                    entries and the full salary ledger can take a moment; the header indicator shows
                    <em> loading&hellip;</em> while data is in flight. If the salary ledger fails to load
                    (network blip, RLS hiccup), that indicator flips to <em>salary ledger failed to load &middot;
                    retry</em> and the KPI tiles are suppressed so half-computed numbers cannot be mistaken
                    for finals.
                  </p>
                  <p style={{ margin: '0 0 6px' }}>
                    <strong>Time view</strong> &mdash; cumulative totals across all projects by calendar month.
                    Adds the firm-wide §2.8 Salary Gap on top so the displayed Loaded Cost matches the firm&rsquo;s
                    actual cash outflow (Σ Loaded Cost + Salary Gap &asymp; salaries paid + employer burden +
                    non-labour overhead).
                  </p>
                  <p style={{ margin: 0 }}>
                    <strong>Stage view</strong> &mdash; lifecycle phase aggregate with <strong>additive bars</strong>.
                    Each stage bar is the cost / income that accrued <em>between the prior reached stage and this one</em>{' '}
                    (per project, then summed across projects). The first reached stage absorbs all activity
                    from project start up to that stage&rsquo;s date; the last reached stage additionally absorbs
                    everything from its date to the project&rsquo;s last data point, so an ongoing-CC project
                    keeps contributing post-CC monthly billings into the CC bar. Summing across all stages
                    equals the portfolio total for projects that reached at least one stage. Stage dates use
                    the same primary-then-invoice-fallback rule as the per-project markers.
                  </p>
                </>
              )}
            </div>
          </BrioAnalyticsSurface>
        )}

        {/* ── Portfolio aggregate (admin only, on-demand) ── */}
        {isAdmin && sortedProjects.length > 0 && (
          <BrioAnalyticsSurface padding="m" style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showAggregate && allTimeEntriesLoaded ? 12 : 0 }}>
              <span style={{ fontSize: 12, fontWeight: brioStyles.fontWeightMedium, color: brioColors.black }}>
                Portfolio Aggregate
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => setShowAggregate(prev => !prev)}
                  className={`brio-btn ${showAggregate ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                  style={{ fontSize: 10, padding: '3px 8px' }}
                >
                  {showAggregate ? 'On' : 'Off'}
                </button>
                {showAggregate && allTimeEntriesLoaded && [
                  { id: 'time', label: 'Time' },
                  { id: 'stage', label: 'Stage' },
                ].map(ax => (
                  <button
                    key={ax.id}
                    onClick={() => setAggregateXAxis(ax.id)}
                    className={`brio-btn ${aggregateXAxis === ax.id ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                    style={{ fontSize: 10, padding: '3px 8px' }}
                  >
                    {ax.label}
                  </button>
                ))}
              </div>
            </div>

            {showAggregate && allTimeEntriesLoaded && (
              <>
                <AggregateKPIBar
                  totalIncome={grandTotals.income}
                  totalCost={grandTotals.expenses}
                  totalProfit={grandTotals.profit}
                  gapToTarget={grandTotals.income - grandTotals.expenses * (1 + essentialProfitPct / 100)}
                  projectCount={aggregateProjects.length}
                  fmt={fmt}
                  salaryGap={grandTotals.salaryGap}
                />

                {/* Aggregate bar bucket override (bar + time view only) */}
                {chartType === 'bar' && aggregateXAxis === 'time' && (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 10, color: brioColors.grey, marginRight: 2 }}>Bucket</span>
                    <div style={{ display: 'inline-flex', borderRadius: 3, overflow: 'hidden', border: `1px solid ${brioColors.border}` }}>
                      {[
                        { id: 'auto', label: 'Auto' },
                        { id: 'fy', label: 'FY' },
                        { id: 'cy', label: 'CY' },
                      ].map(am => (
                        <button key={am.id} onClick={() => setAggregateBarMode(am.id)}
                          style={{ fontSize: 10, padding: '3px 6px', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                            background: aggregateBarMode === am.id ? brioColors.gold : 'transparent',
                            color: aggregateBarMode === am.id ? '#fff' : brioColors.grey }}>
                          {am.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Legend */}
                <div style={{ display: 'flex', gap: 16, marginBottom: 6, fontSize: 10, fontWeight: brioStyles.fontWeightRegular, flexWrap: 'wrap' }}>
                  {chartType === 'bar' && aggregateXAxis === 'time' ? (
                    <>
                      <span style={{ color: brioColors.expense }}>● Direct Cost</span>
                      <span style={{ color: 'rgba(166, 98, 87, 0.65)' }}>● Op. Overhead</span>
                      <span style={{ color: 'rgba(159, 144, 42, 0.55)' }}>● Dormancy</span>
                      <span style={{ color: '#6E3F37' }}>● Internal OH</span>
                      <span
                        title="Salary cost of contracted hours that were not logged to any project. Allocated to projects by hour share within each office-month (same denominator as Op. Overhead)."
                        style={{ color: 'rgba(219, 107, 93, 0.95)', cursor: 'help' }}
                      >
                        ● Salary Gap (alloc.)
                      </span>
                      <span style={{ color: brioColors.income }}>● Income (NL)</span>
                      <span style={{ color: 'rgba(83, 119, 103, 0.55)' }}>● Income (IN)</span>
                    </>
                  ) : chartMode === 'financial' ? (
                    <>
                      <span style={{ color: 'rgba(159, 144, 42, 0.5)' }}>┈ Target ({essentialProfitPct}%)</span>
                      <span style={{ color: brioColors.expense }}>● Loaded Cost</span>
                      <span style={{ color: brioColors.expense, opacity: 0.5 }}>● Direct Cost</span>
                      <span style={{ color: brioColors.income }}>● Income</span>
                    </>
                  ) : (
                    <>
                      <span style={{ color: brioColors.income }}>● Income (+)</span>
                      <span style={{ color: brioColors.expense }}>● Loaded Cost (−)</span>
                      <span style={{ color: brioColors.expense, opacity: 0.5 }}>● Direct Cost (−)</span>
                      <span style={{ color: 'rgba(159, 144, 42, 0.5)' }}>┈ Target (−{essentialProfitPct}%)</span>
                      <span style={{ color: brioColors.gold }}>● Profit</span>
                      <span style={{ color: brioColors.gold, opacity: 0.5 }}>┈ Monthly P/L</span>
                    </>
                  )}
                </div>

                {aggregateXAxis === 'time' ? (
                  chartType === 'bar' ? (
                    <ProfitabilityBarChart
                      data={aggregateBarData.rows}
                      height={280}
                      tickFmt={tickFmt}
                      fmt={fmt}
                    />
                  ) : (
                    <ProfitabilityChart
                      data={aggregateTimeData}
                      chartMode={chartMode}
                      gradientId="agg-time"
                      height={280}
                      tickFmt={tickFmt}
                      fmt={fmt}
                      essentialProfitPct={essentialProfitPct}
                    />
                  )
                ) : (
                  <>
                    <ProfitabilityChart
                      data={aggregateStageData}
                      chartMode={chartMode}
                      gradientId="agg-stage"
                      height={280}
                      tickFmt={tickFmt}
                      fmt={fmt}
                      essentialProfitPct={essentialProfitPct}
                      xDataKey="stage"
                    />
                    <div style={{ fontSize: 9, color: brioColors.grey, fontStyle: 'italic', marginTop: 4 }}>
                      Each stage bar is the cost / income that accrued between the prior reached stage and this one (per project, then summed). The bars are additive: summing across all stages equals the portfolio total. Stage intervals are equal-spaced — this is lifecycle progression, not calendar duration.
                    </div>
                  </>
                )}
              </>
            )}
          </BrioAnalyticsSurface>
        )}

        {/* ── Grand totals bar ── */}
        <BrioAnalyticsSurface padding="m" style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'baseline' }}>
            <div>
              <div style={{ fontSize: 10, color: brioColors.expense, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Expenses</div>
              <div style={{ fontSize: 15, fontWeight: 400, fontVariantNumeric: 'tabular-nums' }}>{fmt(grandTotals.expenses)}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: brioColors.income, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Income</div>
              <div style={{ fontSize: 15, fontWeight: 400, fontVariantNumeric: 'tabular-nums' }}>{fmt(grandTotals.income)}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: grandTotals.profit >= 0 ? brioColors.income : brioColors.expense, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {grandTotals.profit >= 0 ? 'Profit' : 'Loss'}
              </div>
              <div style={{
                fontSize: 15,
                fontWeight: 400,
                fontVariantNumeric: 'tabular-nums',
                color: grandTotals.profit >= 0 ? brioColors.income : brioColors.expense,
              }}>
                {fmt(Math.abs(grandTotals.profit))}
              </div>
            </div>
            {grandTotals.income > 0 && (
              <div>
                <div style={{ fontSize: 10, color: brioColors.grey, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Margin</div>
                <div style={{ fontSize: 15, fontWeight: 400, fontVariantNumeric: 'tabular-nums' }}>
                  {(() => { const pct = (grandTotals.profit / grandTotals.income) * 100; return Math.abs(pct) < 10 ? pct.toFixed(1) : Math.round(pct); })()}%
                </div>
              </div>
            )}
          </div>
        </BrioAnalyticsSurface>

        {/* ── Overhead Reconciliation (activity model only) ── */}
        {activityOverheadEngine.officeMonthReconciliation.length > 0 && (
          <BrioAnalyticsSurface padding="m" style={{ marginTop: 12 }}>
            <div
              onClick={() => setShowReconciliation(prev => !prev)}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {showReconciliation
                ? <ChevronDown size={14} color={brioColors.grey} />
                : <ChevronRight size={14} color={brioColors.grey} />
              }
              <span style={{ fontSize: 12, fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>
                Overhead Reconciliation
              </span>
              <span style={{ fontSize: 10, color: brioColors.grey, fontWeight: brioStyles.fontWeightLight, marginLeft: 4 }}>
                ({activityOverheadEngine.officeMonthReconciliation.length} office-months)
              </span>
            </div>
            {showReconciliation && (() => {
              const recon = activityOverheadEngine.officeMonthReconciliation;
              const debugRows = activityOverheadEngine.projectMonthDebug;
              // Filter reconciliation to selected period if not 'total'
              const filteredRecon = periodType === 'total'
                ? recon
                : recon.filter(r => selectedMonths.includes(r.monthKey));
              const displayRecon = filteredRecon.length > 0 ? filteredRecon : recon;
              const showingAll = filteredRecon.length === 0 && recon.length > 0;

              // Summary totals
              const totalPool = displayRecon.reduce((s, r) => s + r.officeOverheadPool, 0);
              const totalAllocatedOp = displayRecon.reduce((s, r) => s + r.allocatedOperationalOverhead, 0);
              const totalAllocatedDor = displayRecon.reduce((s, r) => s + r.allocatedDormancyCarryingCost, 0);
              const totalUnallocated = totalPool - totalAllocatedOp - totalAllocatedDor;
              const hasMissingMonthly = displayRecon.some(r => r.poolSource === 'missing' || r.poolSource === 'yearly_derived');

              return (
                <div style={{ marginTop: 12 }}>
                  {showingAll && (
                    <div style={{ fontSize: 10, color: brioColors.grey, fontStyle: 'italic', marginBottom: 8 }}>
                      No overhead data in selected period. Showing all available office-months.
                    </div>
                  )}

                  {hasMissingMonthly && (
                    <div style={{ fontSize: 10, color: brioColors.expense, fontStyle: 'italic', marginBottom: 8, padding: '4px 8px', background: 'rgba(166, 98, 87, 0.06)', borderRadius: 4 }}>
                      Some office-months are using yearly/12 fallback or have no overhead data. Look for &quot;yearly&quot; or &quot;missing&quot; in the Source column below.
                    </div>
                  )}

                  {/* Summary row */}
                  <div style={{ display: 'flex', gap: 24, marginBottom: 12, fontSize: 11, flexWrap: 'wrap' }}>
                    <div>
                      <span style={{ color: brioColors.grey, fontSize: 10, textTransform: 'uppercase' }}>Pool (INR) </span>
                      <span style={{ fontWeight: brioStyles.fontWeightMedium, fontVariantNumeric: 'tabular-nums' }}>
                        {Math.round(totalPool).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: brioColors.grey, fontSize: 10, textTransform: 'uppercase' }}>Op. Allocated </span>
                      <span style={{ fontWeight: brioStyles.fontWeightMedium, fontVariantNumeric: 'tabular-nums' }}>
                        {Math.round(totalAllocatedOp).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: brioColors.grey, fontSize: 10, textTransform: 'uppercase' }}>Dormancy Allocated </span>
                      <span style={{ fontWeight: brioStyles.fontWeightMedium, fontVariantNumeric: 'tabular-nums' }}>
                        {Math.round(totalAllocatedDor).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: brioColors.grey, fontSize: 10, textTransform: 'uppercase' }}>Unallocated </span>
                      <span style={{
                        fontWeight: brioStyles.fontWeightMedium,
                        fontVariantNumeric: 'tabular-nums',
                        color: Math.abs(totalUnallocated) > 1 ? brioColors.expense : brioColors.grey,
                      }}>
                        {Math.round(totalUnallocated).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: brioColors.grey, fontSize: 10, textTransform: 'uppercase' }}>Delta </span>
                      <span style={{
                        fontWeight: brioStyles.fontWeightMedium,
                        fontVariantNumeric: 'tabular-nums',
                        color: Math.abs(totalUnallocated) < 1 ? brioColors.income : brioColors.expense,
                      }}>
                        {Math.abs(totalUnallocated) < 1 ? '0 (balanced)' : Math.round(totalUnallocated).toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>

                  {/* Dormancy policy note */}
                  <div style={{ fontSize: 9, color: brioColors.grey, marginBottom: 8, fontStyle: 'italic' }}>
                    Dormancy pool = {(DORMANCY_POOL_PCT * 100).toFixed(0)}% of office-month overhead, distributed evenly across dormant open projects.
                    Unallocated = Pool − Op. Allocated − Dormancy Allocated.
                  </div>

                  {/* Office-month reconciliation table */}
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, fontVariantNumeric: 'tabular-nums' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${brioColors.border}` }}>
                          <th style={{ textAlign: 'left', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Office</th>
                          <th style={{ textAlign: 'left', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Month</th>
                          <th style={{ textAlign: 'left', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Source</th>
                          <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Pool (INR)</th>
                          <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Hours</th>
                          <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Active</th>
                          <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Dormant</th>
                          <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Op. Alloc</th>
                          <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Dor. Alloc</th>
                          <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Unalloc</th>
                          <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Delta</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayRecon.map((r, i) => {
                          const delta = r.officeOverheadPool - r.allocatedOperationalOverhead - r.allocatedDormancyCarryingCost;
                          const hasIssue = r.officeOverheadPool > 0 && r.totalAllocableOfficeHours <= 0;
                          const isMissing = r.poolSource === 'missing';
                          const isYearlyFallback = r.poolSource === 'yearly_derived';
                          return (
                            <tr key={i} style={{
                              borderBottom: `1px solid ${brioColors.border}`,
                              background: isMissing ? 'rgba(166, 98, 87, 0.08)' : hasIssue ? 'rgba(166, 98, 87, 0.04)' : undefined,
                            }}>
                              <td style={{ padding: '3px 6px' }}>{r.officeKey}</td>
                              <td style={{ padding: '3px 6px' }}>{shortMonth(r.monthKey)}</td>
                              <td style={{ padding: '3px 6px', color: isMissing ? brioColors.expense : isYearlyFallback ? '#8b6914' : brioColors.grey, fontStyle: isMissing || isYearlyFallback ? 'italic' : 'normal', fontSize: 9 }}>
                                {r.poolSource === 'monthly' ? 'monthly' : r.poolSource === 'yearly_derived' ? 'yearly' : 'missing'}
                              </td>
                              <td style={{ padding: '3px 6px', textAlign: 'right' }}>{Math.round(r.officeOverheadPool).toLocaleString('en-IN')}</td>
                              <td style={{ padding: '3px 6px', textAlign: 'right' }}>{r.totalAllocableOfficeHours > 0 ? r.totalAllocableOfficeHours.toFixed(1) : '—'}</td>
                              <td style={{ padding: '3px 6px', textAlign: 'right' }}>{r.activeProjectCount || '—'}</td>
                              <td style={{ padding: '3px 6px', textAlign: 'right' }}>{r.dormantProjectCount || '—'}</td>
                              <td style={{ padding: '3px 6px', textAlign: 'right' }}>{Math.round(r.allocatedOperationalOverhead).toLocaleString('en-IN')}</td>
                              <td style={{ padding: '3px 6px', textAlign: 'right' }}>{Math.round(r.allocatedDormancyCarryingCost).toLocaleString('en-IN')}</td>
                              <td style={{ padding: '3px 6px', textAlign: 'right', color: Math.abs(r.unallocatedOverhead) > 1 ? brioColors.expense : brioColors.grey }}>
                                {Math.round(r.unallocatedOverhead).toLocaleString('en-IN')}
                              </td>
                              <td style={{ padding: '3px 6px', textAlign: 'right', color: Math.abs(delta) < 1 ? brioColors.income : brioColors.expense }}>
                                {Math.abs(delta) < 1 ? '0' : Math.round(delta).toLocaleString('en-IN')}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Project-month debug detail (nested collapsible) */}
                  {debugRows.length > 0 && (() => {
                    // Filter debug rows to displayed reconciliation months
                    const reconKeys = new Set(displayRecon.map(r => `${r.officeKey}|${r.monthKey}`));
                    const filteredDebug = debugRows.filter(d => reconKeys.has(`${d.officeKey}|${d.monthKey}`));
                    const zeroReasonRows = filteredDebug.filter(d => d.reasonIfZero);
                    const dormancyRows = filteredDebug.filter(d => d.type === 'dormancy');
                    const allocatedRows = filteredDebug.filter(d => !d.reasonIfZero && d.allocatedOperationalOverhead > 0);

                    return (
                      <details style={{ marginTop: 12 }}>
                        <summary style={{ cursor: 'pointer', fontSize: 10, color: brioColors.grey, fontWeight: brioStyles.fontWeightMedium }}>
                          Project-Month Detail ({filteredDebug.length} entries
                          {zeroReasonRows.length > 0 ? `, ${zeroReasonRows.length} zero-reason` : ''}
                          {dormancyRows.length > 0 ? `, ${dormancyRows.length} dormancy` : ''})
                        </summary>
                        <div style={{ overflowX: 'auto', marginTop: 6 }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, fontVariantNumeric: 'tabular-nums' }}>
                            <thead>
                              <tr style={{ borderBottom: `1px solid ${brioColors.border}` }}>
                                <th style={{ textAlign: 'left', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Project</th>
                                <th style={{ textAlign: 'left', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Office</th>
                                <th style={{ textAlign: 'left', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Month</th>
                                <th style={{ textAlign: 'left', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Type</th>
                                <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Proj Hrs</th>
                                <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Share</th>
                                <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Pool</th>
                                <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Op. Alloc</th>
                                <th style={{ textAlign: 'right', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Dor. Cost</th>
                                <th style={{ textAlign: 'left', padding: '3px 6px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey }}>Note</th>
                              </tr>
                            </thead>
                            <tbody>
                              {/* Show zero-reason rows first, then dormancy, then allocated */}
                              {[...zeroReasonRows, ...dormancyRows, ...allocatedRows].slice(0, 200).map((d, i) => (
                                <tr key={i} style={{
                                  borderBottom: `1px solid ${brioColors.border}`,
                                  background: d.reasonIfZero ? 'rgba(166, 98, 87, 0.04)' : d.type === 'dormancy' ? 'rgba(139, 105, 20, 0.04)' : undefined,
                                }}>
                                  <td style={{ padding: '3px 6px', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.projectName}</td>
                                  <td style={{ padding: '3px 6px' }}>{d.officeKey}</td>
                                  <td style={{ padding: '3px 6px' }}>{shortMonth(d.monthKey)}</td>
                                  <td style={{ padding: '3px 6px', fontSize: 9, color: d.type === 'dormancy' ? '#8b6914' : brioColors.grey }}>{d.type}</td>
                                  <td style={{ padding: '3px 6px', textAlign: 'right' }}>{d.projectAllocableHours > 0 ? d.projectAllocableHours.toFixed(1) : '—'}</td>
                                  <td style={{ padding: '3px 6px', textAlign: 'right' }}>{d.projectHourShare > 0 ? `${(d.projectHourShare * 100).toFixed(1)}%` : '—'}</td>
                                  <td style={{ padding: '3px 6px', textAlign: 'right' }}>{Math.round(d.officeOverheadPool).toLocaleString('en-IN')}</td>
                                  <td style={{ padding: '3px 6px', textAlign: 'right' }}>{d.allocatedOperationalOverhead > 0 ? Math.round(d.allocatedOperationalOverhead).toLocaleString('en-IN') : '—'}</td>
                                  <td style={{ padding: '3px 6px', textAlign: 'right', color: '#8b6914' }}>{d.dormancyCarryingCost > 0 ? Math.round(d.dormancyCarryingCost).toLocaleString('en-IN') : '—'}</td>
                                  <td style={{ padding: '3px 6px', color: d.reasonIfZero ? brioColors.expense : brioColors.grey, fontStyle: d.reasonIfZero ? 'italic' : 'normal', fontSize: 9 }}>
                                    {d.reasonIfZero || (d.poolSource !== 'monthly' ? d.poolSource : '—')}
                                  </td>
                                </tr>
                              ))}
                              {[...zeroReasonRows, ...dormancyRows, ...allocatedRows].length > 200 && (
                                <tr>
                                  <td colSpan={10} style={{ padding: '3px 6px', fontSize: 10, color: brioColors.grey, fontStyle: 'italic' }}>
                                    Showing first 200 of {[...zeroReasonRows, ...dormancyRows, ...allocatedRows].length} rows
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    );
                  })()}
                </div>
              );
            })()}
          </BrioAnalyticsSurface>
        )}

        {/* ── Multi-project selection ── */}
        {sortedProjects.length > 1 && (
          <BrioAnalyticsSurface padding="m" style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: brioStyles.fontWeightMedium, color: brioColors.black }}>
                Compare Projects
              </span>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {selectedProjects.length >= 2 && (
                  <>
                    {[
                      { id: 'compare', label: 'Compare Individually' },
                      { id: 'combined', label: 'Show Combined' },
                    ].map(mm => (
                      <button
                        key={mm.id}
                        onClick={() => setMultiProjectMode(mm.id)}
                        className={`brio-btn ${multiProjectMode === mm.id ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                        style={{ fontSize: 10, padding: '3px 8px' }}
                      >
                        {mm.label}
                      </button>
                    ))}
                  </>
                )}
                {selectedProjects.length > 0 && (
                  <button
                    onClick={() => setSelectedProjectIds({})}
                    className="brio-btn brio-btn-secondary"
                    style={{ fontSize: 10, padding: '3px 8px' }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Project chips */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: selectedProjects.length > 0 ? 12 : 0 }}>
              {sortedProjects.map(p => {
                const sel = !!selectedProjectIds[p.id];
                return (
                  <button
                    key={p.id}
                    onClick={() => toggleProjectSelection(p.id)}
                    style={{
                      fontSize: 10,
                      padding: '3px 8px',
                      borderRadius: 3,
                      border: sel ? `1px solid ${brioColors.gold}` : `1px solid ${brioColors.border}`,
                      background: sel ? brioColors.goldLight : 'transparent',
                      color: sel ? brioColors.gold : brioColors.grey,
                      cursor: 'pointer',
                      fontFamily: brioStyles.fontFamily,
                      fontWeight: sel ? brioStyles.fontWeightMedium : brioStyles.fontWeightRegular,
                    }}
                  >
                    {p.projectCode || p.name}
                  </button>
                );
              })}
            </div>

            {/* Comparison / combined chart */}
            {selectedProjects.length === 0 && (
              <div style={{ fontSize: 11, color: brioColors.grey, fontStyle: 'italic', padding: '8px 0' }}>
                Select projects above to compare
              </div>
            )}

            {selectedProjects.length >= 2 && multiProjectMode === 'compare' && (
              <>
                {selectedProjects.length > 8 && (
                  <div style={{ fontSize: 10, color: brioColors.expense, marginBottom: 6 }}>
                    Showing first 8 of {selectedProjects.length} selected projects for readability.
                  </div>
                )}
                {/* Legend */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 6, fontSize: 10 }}>
                  {selectedProjects.slice(0, 8).map((p, i) => (
                    <span key={p.id} style={{ color: COMPARE_COLORS[i % COMPARE_COLORS.length] }}>
                      ● {p.projectCode || p.name}
                    </span>
                  ))}
                </div>
                <CompareChart
                  mergedData={selectedCompareData}
                  projects={selectedProjects.slice(0, 8)}
                  height={280}
                  tickFmt={tickFmt}
                  fmt={fmt}
                  domain={scaleMode === 'shared' ? sharedYDomain : undefined}
                />
              </>
            )}

            {selectedProjects.length >= 2 && multiProjectMode === 'combined' && (
              <>
                <div style={{ display: 'flex', gap: 16, marginBottom: 6, fontSize: 10, fontWeight: brioStyles.fontWeightRegular, flexWrap: 'wrap' }}>
                  {chartMode === 'financial' ? (
                    <>
                      <span style={{ color: brioColors.income }}>● Income</span>
                      <span style={{ color: brioColors.expense }}>● Loaded Cost</span>
                      <span style={{ color: brioColors.gold }}>● Profit</span>
                      <span style={{ color: 'rgba(159, 144, 42, 0.5)' }}>┈ Target ({essentialProfitPct}%)</span>
                    </>
                  ) : (
                    <>
                      <span style={{ color: brioColors.gold }}>● Profit</span>
                      <span style={{ color: 'rgba(159, 144, 42, 0.5)' }}>┈ Target ({essentialProfitPct}%)</span>
                    </>
                  )}
                </div>
                <ProfitabilityChart
                  data={selectedCombinedData}
                  chartMode={chartMode}
                  gradientId="sel-combined"
                  height={280}
                  tickFmt={tickFmt}
                  fmt={fmt}
                  essentialProfitPct={essentialProfitPct}
                  domain={scaleMode === 'shared' ? sharedYDomain : undefined}
                />
              </>
            )}

            {selectedProjects.length === 1 && (
              <div style={{ fontSize: 11, color: brioColors.grey, fontStyle: 'italic', padding: '8px 0' }}>
                Select at least 2 projects to compare
              </div>
            )}
          </BrioAnalyticsSurface>
        )}

        {/* ── Loading indicator ── */}
        {dataLoading && (
          <BrioAnalyticsSurface padding="m" style={{ marginTop: 12 }}>
            <p style={{
              fontSize: brioStyles.fontSizeBase,
              fontWeight: brioStyles.fontWeightLight,
              color: brioColors.grey,
              margin: 0,
              fontStyle: 'italic',
            }}>
              loading profitability data…
            </p>
          </BrioAnalyticsSurface>
        )}

        {/* ── Project cards ──
            Hidden while any data source is still loading. Previously the cards
            rendered with whatever subset of time entries had arrived, which made
            project values look empty/zero and forced users to navigate away and
            back to see complete data. */}
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!dataLoading && [...billedProjects, ...nonBilledProjects].map((project, idx) => {
            const data = projectDataMap[project.id];
            if (!data) return null;
            const client = activeClients.find(c => c.id === project.clientId);
            const expanded = expandedProjects[project.id];
            const profitColor = data.profit >= 0 ? brioColors.income : brioColors.expense;
            const marginPct = data.totalIncome > 0 ? (data.profit / data.totalIncome) * 100 : null;
            const margin = marginPct != null ? (Math.abs(marginPct) < 10 ? marginPct.toFixed(1) : String(Math.round(marginPct))) : '—';
            const isFirstNonBilled = idx === billedProjects.length && nonBilledProjects.length > 0;

            // CC retainer uses configured months regardless of status so paused projects still show full billing potential.
            // Fee fields are stored in the project's contract currency, so convert
            // them before formatting in display currency.
            const feeCurrency = project?.contractCurrency;
            const baseFee = contractToDisplay(Number(project?.budget) || 0, feeCurrency);
            const ccMonthly = contractToDisplay(Number(project?.retainerConfig?.monthlyAmount) || 0, feeCurrency);
            const ccMonths = Number(project?.retainerConfig?.totalMonths) || 0;
            const ccRetainer = ccMonthly * ccMonths;
            const miscFee = contractToDisplay(
              (Array.isArray(project?.miscFeeScopes) ? project.miscFeeScopes : [])
                .reduce((s, x) => s + (Number(x.amount) || 0), 0),
              feeCurrency,
            );
            const totalDesignFee = baseFee + ccRetainer + miscFee;
            const feeTooltip = `Design Fee = Base ${fmt(baseFee)} + CC Retainer ${fmt(ccRetainer)} (${ccMonths} mo × ${fmt(ccMonthly)}) + Misc ${fmt(miscFee)}`;

            return (
              <React.Fragment key={project.id}>
                {isFirstNonBilled && (
                  <div style={{
                    marginTop: 8,
                    marginBottom: 4,
                    paddingBottom: 6,
                    borderBottom: `1px solid ${brioColors.border}`,
                  }}>
                    <h4 className="brio-section-header__title" style={{ margin: 0, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: brioColors.grey }}>
                      Non-Billed Projects
                    </h4>
                  </div>
                )}
              <BrioAnalyticsSurface padding="m">
                {/* Project header — always visible */}
                <div
                  onClick={() => toggleProject(project.id)}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
                >
                  {expanded
                    ? <ChevronDown size={14} color={brioColors.grey} />
                    : <ChevronRight size={14} color={brioColors.grey} />
                  }
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: brioStyles.fontWeightMedium, fontSize: 13 }}>
                      {project.projectCode || project.name}
                    </span>
                    {client && (
                      <span style={{ fontSize: 11, color: brioColors.grey, marginLeft: 8 }}>
                        {client.organisation || client.name}
                      </span>
                    )}
                  </div>

                  {/* Summary numbers */}
                  <div style={{ display: 'flex', gap: 20, alignItems: 'baseline', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                    <span style={{ color: brioColors.gold, fontWeight: brioStyles.fontWeightMedium }} title={feeTooltip}>
                      <span style={{ color: brioColors.grey, fontSize: 10, fontWeight: brioStyles.fontWeightRegular, marginRight: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Fee
                      </span>
                      {fmt(totalDesignFee)}
                    </span>
                    <span style={{ color: brioColors.expense }}>{fmt(data.totalExpenses)}</span>
                    <span style={{ color: brioColors.income }}>{fmt(data.totalIncome)}</span>
                    <span style={{ color: profitColor, fontWeight: brioStyles.fontWeightMedium }}>
                      {data.profit >= 0 ? '' : '−'}{fmt(Math.abs(data.profit))}
                    </span>
                    <span style={{ color: brioColors.grey, fontSize: 11 }}>
                      {margin}%
                    </span>
                  </div>
                </div>

                {/* Expanded: chart + details */}
                {expanded && (
                  <div style={{ marginTop: 16 }}>
                    {/* Legend */}
                    <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 10, fontWeight: brioStyles.fontWeightRegular, flexWrap: 'wrap' }}>
                      {chartType === 'bar' ? (
                        <>
                          <span style={{ color: brioColors.expense }}>● Direct Cost</span>
                          <span style={{ color: 'rgba(166, 98, 87, 0.65)' }}>● Op. Overhead</span>
                          <span style={{ color: 'rgba(159, 144, 42, 0.55)' }}>● Dormancy</span>
                          <span style={{ color: '#6E3F37' }}>● Internal OH</span>
                          <span
                            title="Salary cost of contracted hours that were not logged to any project. Allocated to this project by hour share within the office-month."
                            style={{ color: 'rgba(219, 107, 93, 0.95)', cursor: 'help' }}
                          >
                            ● Salary Gap (alloc.)
                          </span>
                          <span style={{ color: brioColors.income }}>● Income (NL)</span>
                          <span style={{ color: 'rgba(83, 119, 103, 0.55)' }}>● Income (IN)</span>
                        </>
                      ) : chartMode === 'financial' ? (
                        <>
                          <span style={{ color: 'rgba(159, 144, 42, 0.5)' }}>┈ Target ({essentialProfitPct}%)</span>
                          <span style={{ color: brioColors.expense }}>● Loaded Cost</span>
                          <span style={{ color: brioColors.expense, opacity: 0.5 }}>● Direct Cost</span>
                          <span style={{ color: brioColors.income }}>● Income</span>
                          {totalDesignFee > 0 && (
                            <span style={{ color: brioColors.gold }}>┈ Fee</span>
                          )}
                        </>
                      ) : (
                        <>
                          <span style={{ color: brioColors.income }}>● Income (+)</span>
                          <span style={{ color: brioColors.expense }}>● Loaded Cost (−)</span>
                          <span style={{ color: brioColors.expense, opacity: 0.5 }}>● Direct Cost (−)</span>
                          <span style={{ color: 'rgba(159, 144, 42, 0.5)' }}>┈ Target (−{essentialProfitPct}%)</span>
                          <span style={{ color: brioColors.gold }}>● Profit</span>
                        </>
                      )}
                    </div>

                    {/* Chart */}
                    {chartType === 'bar' ? (
                      <ProfitabilityBarChart
                        data={projectBarDataMap[project.id]?.rows || []}
                        height={240}
                        tickFmt={tickFmt}
                        fmt={fmt}
                      />
                    ) : (
                      <ProfitabilityChart
                        data={data.cumulativeData}
                        chartMode={chartMode}
                        gradientId={`proj-${project.id}`}
                        height={240}
                        tickFmt={tickFmt}
                        fmt={fmt}
                        essentialProfitPct={essentialProfitPct}
                        domain={scaleMode === 'shared' ? sharedYDomain : undefined}
                        stageMarkers={stageMarkersMap[project.id]}
                        designFee={totalDesignFee}
                      />
                    )}

                    {/* Monthly breakdown drawer (collapsed by default) */}
                    {data.chartData.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div
                          onClick={() => toggleLedger(project.id)}
                          style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: 11,
                            color: brioColors.grey,
                            fontWeight: brioStyles.fontWeightMedium,
                            padding: '4px 0',
                          }}
                        >
                          {expandedLedgers[project.id]
                            ? <ChevronDown size={12} />
                            : <ChevronRight size={12} />
                          }
                          Monthly breakdown
                        </div>
                        {expandedLedgers[project.id] && (() => {
                          // Always show all cost columns even when totals are zero,
                          // so a missing column never gets confused with a calc bug.
                          const showOH = true;
                          const showDor = true;
                          const showIntOH = true;
                          const showSalaryGap = true;
                          return (
                          <div style={{ overflowX: 'auto', marginTop: 4 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
                              <thead>
                                <tr style={{ borderBottom: `1px solid ${brioColors.border}` }}>
                                  <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey, fontSize: 10 }}>Month</th>
                                  <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.expense, fontSize: 10 }}>Direct Cost</th>
                                  {showOH && (
                                    <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: brioStyles.fontWeightMedium, color: '#7c6f1a', fontSize: 10 }}>Op. Overhead</th>
                                  )}
                                  {showDor && (
                                    <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: brioStyles.fontWeightMedium, color: '#8b6914', fontSize: 10 }}>Dormancy Cost</th>
                                  )}
                                  {showIntOH && (
                                    <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: brioStyles.fontWeightMedium, color: '#5d4037', fontSize: 10 }}>Internal OH</th>
                                  )}
                                  {showSalaryGap && (
                                    <th
                                      title="Salary cost of contracted hours that were not logged to any project. Allocated to this project by hour share within the office-month."
                                      style={{ textAlign: 'right', padding: '4px 8px', fontWeight: brioStyles.fontWeightMedium, color: 'rgba(219, 107, 93, 0.95)', fontSize: 10, cursor: 'help' }}
                                    >
                                      Salary Gap
                                    </th>
                                  )}
                                  <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.expense, fontSize: 10 }}>Loaded Cost</th>
                                  <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.income, fontSize: 10 }}>Income</th>
                                  <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: brioStyles.fontWeightMedium, color: brioColors.grey, fontSize: 10 }}>Profit</th>
                                </tr>
                              </thead>
                              <tbody>
                                {data.chartData.map((row, i) => {
                                  const rowProfit = row.income - row.cost;
                                  return (
                                    <tr key={i} style={{ borderBottom: `1px solid ${brioColors.border}` }}>
                                      <td style={{ padding: '4px 8px', fontWeight: brioStyles.fontWeightRegular }}>{row.month}</td>
                                      <td style={{ padding: '4px 8px', textAlign: 'right', color: brioColors.expense }}>{row.directCost > 0 ? fmt(row.directCost) : '—'}</td>
                                      {showOH && (
                                        <td style={{ padding: '4px 8px', textAlign: 'right', color: '#7c6f1a' }}>{row.overhead > 0 ? fmt(row.overhead) : '—'}</td>
                                      )}
                                      {showDor && (
                                        <td style={{ padding: '4px 8px', textAlign: 'right', color: '#8b6914' }}>{row.dormancy > 0 ? fmt(row.dormancy) : '—'}</td>
                                      )}
                                      {showIntOH && (
                                        <td style={{ padding: '4px 8px', textAlign: 'right', color: '#5d4037' }}>{row.internalOH > 0 ? fmt(row.internalOH) : '—'}</td>
                                      )}
                                      {showSalaryGap && (
                                        <td style={{ padding: '4px 8px', textAlign: 'right', color: 'rgba(219, 107, 93, 0.95)' }}>{row.salaryGap > 0 ? fmt(row.salaryGap) : '—'}</td>
                                      )}
                                      <td style={{ padding: '4px 8px', textAlign: 'right', color: brioColors.expense }}>{row.cost > 0 ? fmt(row.cost) : '—'}</td>
                                      <td style={{ padding: '4px 8px', textAlign: 'right', color: brioColors.income }}>{row.income > 0 ? fmt(row.income) : '—'}</td>
                                      <td style={{ padding: '4px 8px', textAlign: 'right', color: rowProfit >= 0 ? brioColors.income : brioColors.expense }}>
                                        {row.cost > 0 || row.income > 0 ? fmt(rowProfit) : '—'}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr style={{ borderTop: `2px solid ${brioColors.black}` }}>
                                  <td style={{ padding: '6px 8px', fontWeight: brioStyles.fontWeightMedium }}>Total</td>
                                  <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: brioStyles.fontWeightMedium, color: brioColors.expense }}>{fmt(data.totalDirectCost)}</td>
                                  {showOH && (
                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: brioStyles.fontWeightMedium, color: '#7c6f1a' }}>{fmt(data.totalOverhead)}</td>
                                  )}
                                  {showDor && (
                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: brioStyles.fontWeightMedium, color: '#8b6914' }}>{fmt(data.totalDormancy)}</td>
                                  )}
                                  {showIntOH && (
                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: brioStyles.fontWeightMedium, color: '#5d4037' }}>{fmt(data.totalInternalOH)}</td>
                                  )}
                                  {showSalaryGap && (
                                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: brioStyles.fontWeightMedium, color: 'rgba(219, 107, 93, 0.95)' }}>{fmt(data.totalSalaryGap || 0)}</td>
                                  )}
                                  <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: brioStyles.fontWeightMedium, color: brioColors.expense }}>{fmt(data.totalExpenses)}</td>
                                  <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: brioStyles.fontWeightMedium, color: brioColors.income }}>{fmt(data.totalIncome)}</td>
                                  <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: brioStyles.fontWeightMedium, color: profitColor }}>{fmt(data.profit)}</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </BrioAnalyticsSurface>
              </React.Fragment>
            );
          })}

          {sortedProjects.length === 0 && (
            <BrioAnalyticsSurface padding="m">
              <div style={{ fontSize: 12, color: brioColors.grey, textAlign: 'center', padding: 24 }}>
                No projects with expenses or income in the selected period
              </div>
            </BrioAnalyticsSurface>
          )}
        </div>

        </div>
      </BrioFrame>
    </div>
  );
}
